import { memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { PlayClipNode }          from './nodes/PlayClipNode'
import { BlendNode }             from './nodes/BlendNode'
import { BlendByBoolNode }       from './nodes/BlendByBoolNode'
import { FinalPoseNode }         from './nodes/FinalPoseNode'
import { LiveLinkNode }          from './nodes/LiveLinkNode'
import { FirstFramePoseNode, LastFramePoseNode, FACE_FRAME_POSE_COLOR } from './nodes/FramePoseNodes'
import { RigControllerNode, RIG_CONTROLLER_COLOR } from './nodes/RigControllerNode'
import { RigPoseNode, RigMacroNode, RigControlNode, RigBlendNode } from './nodes/RigChainNodes'
import { HeadFollowNode }        from './nodes/HeadFollowNode'
import { EyeFollowNode }         from './nodes/EyeFollowNode'
import { BlinkNode }             from './nodes/BlinkNode'
import { DeleteEdge }            from './nodes/DeleteEdge'
import { StateMachineNode }      from './nodes/StateMachineNode'
import { RandomNode }            from './nodes/RandomNode'
import { SelectNode }            from './nodes/SelectNode'
import { GetVariableNode }       from './nodes/GetVariableNode'
import { LayeredBoneBlendNode }  from './nodes/LayeredBoneBlendNode'
import { PrefabSlotNode }        from './nodes/PrefabSlotNode'
import NodeContextMenu           from './nodes/NodeContextMenu'
import { GraphToolbar }          from './GraphToolbar'
import { StateMachineEditor }    from './StateMachineEditor'
import { TransitionRuleEditor }  from './TransitionRuleEditor'
import { EventGraph }            from './EventGraph'
// Legacy CharacterMaterialEditor removed in Phase 7b — material authoring
// now lives entirely in the content browser (MaterialInspector + RigInspector).
import { CharacterCreationWizard } from './CharacterCreationWizard'
import { PrefabEditorPanel } from './PrefabEditorPanel'
import { AudioAssetsPanel } from './AudioAssetsPanel'
import { AnimatorEditorPanel } from './AnimatorEditorPanel'
import { GraphSearchBox }        from './GraphSearchBox'
import { useCanvasFocus, useFocusDrill, selectOnlyChanges } from '../lib/useGraphFocus'
import { findGraphNodes } from '../lib/graphNodeSearch'
import { requestFocus } from '../lib/graphFocus'
import { readGraphs } from '../lib/liveGraphs'
import { useRememberedViewport } from '../lib/useGraphViewport'
import PaletteGraphMatches from './blueprint/PaletteGraphMatches'
import { cancelFocus }           from '../lib/graphFocus'
import { graphState }            from '../lib/graphState'
import { faceGraphState }        from '../lib/faceGraphState'
import { eventGraphState, notifyEventGraphChange, subscribeEventGraphInsert } from '../lib/eventGraphState'
import { customEventNames } from '../lib/customEvents'
import { subscribeFaceGraphInsert } from '../lib/faceGraphState'
import { initSM }                from '../lib/smRuntime'
import { syncVariables }         from '../lib/animVariables'
import { tracePhase }            from '../lib/perfProbe'
import { debugState }            from '../lib/debugState'
import { loadBlueprint, getStorageKeys, getStorageKeysFor, resetStorageKeys, saveBlueprintToDB, loadBlueprintFromDB, resolveEditorBlueprintSource, getActiveBlueprintId } from '../lib/loadBlueprint'
import { assetRegistry } from '../lib/assetRegistry'
import { starterTemplateUrl, resolveStarterKind } from '../lib/starterBlueprints'
import { migrateEventNodes, migrateEventNodesAndEdges } from '../lib/blueprintMigrations'
import { dropDisplacedEdges } from '../lib/graphConnectPolicy'
import { useGraphClipboard } from '../lib/useGraphClipboard'
import { createMinter, maxOwnedId } from '../lib/nodeClipboard'
import { availableRefsFor } from '../lib/graphAvailableRefs'
import GraphClipboardStatus from './GraphClipboardStatus'
import { GRAPH_MULTISELECT_PROPS } from '../lib/graphInteractionProps'
import { alignNodes, distributeNodes, straightenConnections, tidyGraph, arrangeShortcutFor } from '../lib/graphArrange'
import { useStableNodeData } from '../lib/useStableNodeData'
import { createGraphHistory, isUndoKeyEvent } from '../lib/graphHistory'
import { isEmptyBlueprintData } from '../lib/blueprintEmpty'
import { setCurrentCharacterId, clearCharactersCache, resolveCharacterId, getCurrentCharacter, isSplatCharacter, isStreamCharacter } from '../lib/characterState'
import { eyeFollowSupport } from '../lib/eyeFollowSupport.js'
import { IS_LOCAL } from '../lib/devServer'  // used for logging only
import { dialog }   from '../lib/dialog'
import { getPendingRigFile, clearPendingRigFile, syncDraftToCharacterStore, getDraft, clearDraft, markDraftSaved, updateDraft, getPendingTextureFiles, clearPendingTextureFile, clearAllPendingTextureFiles } from '../lib/characterAuthoringStore'
import { toProxiedAssetUrl } from '../lib/characterAssetUrl'
import { PluginNode } from './nodes/PluginNode'
import { listPluginNodesByGraph, getPluginNode, subscribePluginRegistry, getPluginRegistryVersion } from '../lib/pluginNodeRegistry'
import { upsertCharacter, uploadTexture, getLatestBlueprint } from '../lib/assetManagerApi.js'
import { mergeBlueprintForSave } from '../lib/blueprintSaveGuard.js'
import { postFileWithProgress } from '../lib/upload.js'
import { isBlueprintCollabCoeditEnabled } from '../lib/featureFlags.js'
import { registerCollabBlueprintClient, isBlueprintCollabActive, writeBlueprintCollab, flushCollabBlueprint, onCollabSyncChange, setRemoteBlueprintHandler } from '../lib/collabBlueprintBridge.js'
// The editor publishes its commands here; the app menubar renders them. See the
// EditorCommands comment below for why the menubar is no longer the editor's.
import { menubarCommands } from '../lib/menubarCommands.js'
import { computeRemoteApplyState, shouldRouteEditToDoc } from '../lib/collabBlueprintSync.js'
import { useAuth } from '../lib/auth/AuthContext.jsx'
import AnchoredOverlay from './AnchoredOverlay'

const AAM_BASE = import.meta.env.VITE_ASSET_MANAGER_URL || 'http://localhost:8081'
// X-API-Key is a localhost-dev affordance — cookie can't reach localhost.
const AAM_KEY  = import.meta.env.VITE_ASSET_MANAGER_API_KEY || ''

// Built-in node-type → React-component map. Plugin-contributed nodes are
// merged on top in getNodeTypes() / getPalette() below; all plugin types
// share the generic PluginNode card driven by their manifest.
const BUILTIN_NODE_TYPES = {
  playClip:     PlayClipNode,
  blend:        BlendNode,
  blendByBool:  BlendByBoolNode,
  finalPose:    FinalPoseNode,
  liveLink:     LiveLinkNode,
  firstFramePose: FirstFramePoseNode,
  lastFramePose:  LastFramePoseNode,
  rigController: RigControllerNode,
  rigPose:      RigPoseNode,
  rigMacro:     RigMacroNode,
  rigControl:   RigControlNode,
  rigBlend:     RigBlendNode,
  headFollow:   HeadFollowNode,
  eyeFollow:    EyeFollowNode,
  blink:        BlinkNode,
  stateMachine: StateMachineNode,
  random:       RandomNode,
  select:       SelectNode,
  getVariable:       GetVariableNode,
  layeredBoneBlend:  LayeredBoneBlendNode,
  prefabSlot:        PrefabSlotNode,
}

// NODE_TYPES is read by ReactFlow's `nodeTypes` prop. We rebuild it lazily
// so plugins registered between hot reloads / page loads are picked up.
function buildNodeTypesForGraph(graph) {
  const map = { ...BUILTIN_NODE_TYPES }
  // The body/face graphs share the same NODE_TYPES map (the BlueprintEditor
  // mounts two ReactFlow canvases). Each plugin node declares which graph
  // it targets in its manifest; we merge entries matching `graph` here.
  for (const entry of listPluginNodesByGraph(graph)) {
    map[entry.nodeManifest.type] = PluginNode
  }
  return map
}

// NODE_TYPES used to be computed at module load — but plugins register
// asynchronously after this module evaluates, so plugin types were never
// in the map and ReactFlow fell back to its default renderer (an empty
// 21×150 rectangle with two handles). Compute lazily per render where
// it's consumed; the union is tiny so the cost is negligible.
function buildNodeTypesAll() {
  return {
    ...BUILTIN_NODE_TYPES,
    ...buildNodeTypesForGraph('body'),
    ...buildNodeTypesForGraph('face'),
  }
}
const EDGE_TYPES = { default: DeleteEdge }


const INITIAL_NODES = [{
  id: 'final', type: 'finalPose',
  position: { x: 500, y: 200 },
  data: { connected: false },
  deletable: false,
}]

// Head Follow and Eye Follow share one palette colour: they are two halves of
// the same "look at the camera" family (Eye Follow even hands its neck share to
// Head Follow's chain), and node colour is category coding here. One named
// constant rather than the literal twice — this file is at its palette-lock
// count, and that lock only ever shrinks (ENG-880).
const FOLLOW_NODE_COLOR = '#30c0e0'

const BUILTIN_NODE_PALETTE = [
  { type: 'playClip',     label: 'Play Clip',     desc: 'Play a single animation clip',           color: '#e07040', category: 'Animation' },
  { type: 'prefabSlot',   label: 'Prefab Slot',   desc: 'Activates when Play Prefab fires — anim output + is-active pin for blending', color: '#c080ff', category: 'Animation' },
  { type: 'stateMachine', label: 'State Machine',  desc: 'Node-based state machine graph',         color: '#7080e0', category: 'Animation' },
  { type: 'liveLink',     label: 'Live Link',      desc: 'Real-time ARKit blendshape stream',      color: '#30d090', category: 'Animation' },
  { type: 'firstFramePose', label: 'First Frame Pose', desc: 'Static first frame of a sentence (via Stream First Frame)', color: FACE_FRAME_POSE_COLOR, category: 'Animation' },
  { type: 'lastFramePose',  label: 'Last Frame Pose',  desc: 'Static last frame of a sentence (via Stream Last Frame)',   color: FACE_FRAME_POSE_COLOR, category: 'Animation' },
  { type: 'rigController', label: 'Rig Controller', desc: 'Manual face-rig sliders — pose the face, or pass LiveLink through', color: RIG_CONTROLLER_COLOR, category: 'Animation' },
  { type: 'rigPose',    label: 'Rig Pose',    desc: 'A whole face configuration — a trained pose from the bundle, or one saved into the node', color: RIG_CONTROLLER_COLOR, category: 'Rig' },
  { type: 'rigMacro',   label: 'Rig Macro',   desc: 'One value drives a whole subset (mouth open, eyes close)',                                  color: RIG_CONTROLLER_COLOR, category: 'Rig' },
  { type: 'rigControl', label: 'Rig Control', desc: 'Drive ONE named control on top of the pose beneath it',                                     color: RIG_CONTROLLER_COLOR, category: 'Rig' },
  { type: 'rigBlend',   label: 'Rig Blend',   desc: 'Crossfade two rig chains by alpha or variable',                                             color: RIG_CONTROLLER_COLOR, category: 'Rig' },
  { type: 'headFollow',   label: 'Head Follow',    desc: 'Rotate neck chain to follow the camera', color: FOLLOW_NODE_COLOR, category: 'Animation' },
  { type: 'eyeFollow',    label: 'Eye Follow',     desc: 'Point the eyes at the camera, with a share of the aim handed to the neck (70/30 by default)', color: FOLLOW_NODE_COLOR, category: 'Animation' },
  { type: 'blend',        label: 'Blend',          desc: 'Blend two poses by alpha or variable',   color: '#40a0e0', category: 'Blend' },
  { type: 'blendByBool', label: 'Blend By Bool',  desc: 'Switch between pose A (false) and pose B (true) driven by a bool pin or variable', color: '#60c060', category: 'Blend' },
  { type: 'random',       label: 'Random',         desc: 'Generate a random float or int value',                       color: '#c080e0', category: 'Math' },
  { type: 'select',       label: 'Select',         desc: 'Select one of N animations by index (variable or wired)',     color: '#e09030', category: 'Math' },
  { type: 'getVariable',      label: 'Get Variable',        desc: 'Read an EventGraph variable as a number',                    color: '#a060d0', category: 'Variable' },
  { type: 'layeredBoneBlend', label: 'Layered Bone Blend',  desc: 'Blend two poses from a specified bone downward (e.g. upper body override)', color: '#90c030', category: 'Blend' },
]

// Plugin nodes contribute palette entries derived from their manifest.
// Category defaults to "Plugins / <plugin name>" so they cluster together
// in the right-click menu and never collide with built-in categories.
function buildPalette() {
  const fromBody = listPluginNodesByGraph('body').map(e => paletteEntryFromManifest(e))
  const fromFace = listPluginNodesByGraph('face').map(e => paletteEntryFromManifest(e))
  return [...BUILTIN_NODE_PALETTE, ...fromBody, ...fromFace]
}
function paletteEntryFromManifest(entry) {
  const m = entry.nodeManifest
  return {
    type: m.type,
    label: m.label ?? m.type,
    desc: m.description ?? `Plugin node from ${entry.pluginName}`,
    color: m.color ?? '#a060d0',
    category: m.group ?? `Plugins / ${entry.pluginName}`,
    _isPlugin: true,
    _manifest: m,
  }
}
// NODE_PALETTE used to be computed at module-load via buildPalette(), but
// plugins register asynchronously AFTER this module evaluates — leaving
// plugin entries permanently missing from the search results. The palette
// is now rebuilt per render where it's actually consumed (palette UI in
// the right-click context menu). Negligible cost; correctness wins.

// Event types that must be unique — deduplicate on load to fix stale saves
const UNIQUE_EVENT_TYPES = new Set(['beginPlay'])

// Node types removed from the system — strip from stale localStorage saves
const REMOVED_NODE_TYPES = new Set([
  'startRecording', 'stopRecording',
  'connectWebSocket', 'disconnectWebSocket',
  // Camera presets are authored graphs now (Camera Rigs in the palette), not an enum.
  // A stale save can still hold this node; its type is no longer registered, so leaving it
  // in would render a broken node. Dropping it is safe precisely because setCameraPreset
  // never shipped past this branch — no user blueprint can lose behaviour it relied on.
  'setCameraPreset',
])

// Handle ids renamed between versions — map old → new
const HANDLE_RENAMES = { 'anim': 'out-animName' }

function deduplicateNodes(nodes) {
  const seen = new Set()
  return nodes.filter(n => {
    if (seen.has(n.id)) return false
    seen.add(n.id)
    return true
  })
}

function sanitizeEventGraph(nodes, edges, functions = {}) {
  const filtered = deduplicateNodes(nodes.filter(n => !REMOVED_NODE_TYPES.has(n.type)))
  const kept = new Set(filtered.map(n => n.id))
  const cleanEdges = edges
    .filter(e => kept.has(e.source) && kept.has(e.target))
    .map(e => ({
      ...e,
      sourceHandle: HANDLE_RENAMES[e.sourceHandle] ?? e.sourceHandle,
      targetHandle: HANDLE_RENAMES[e.targetHandle] ?? e.targetHandle,
    }))
  const cleanFunctions = Object.fromEntries(
    Object.entries(functions).map(([id, fn]) => [
      id,
      { ...fn, nodes: deduplicateNodes(fn.nodes ?? []) },
    ])
  )
  return { eventNodes: filtered, eventEdges: cleanEdges, functions: cleanFunctions }
}

// migrateEventNodes (onAnimation -> onGesture, beginPlay -> onConstruct,
// recursing into graphGroup innerNodes) is imported from
// ../lib/blueprintMigrations — one tested pass shared with loadBlueprint.js.

function loadEvents(sk) {
  try {
    const raw = localStorage.getItem(sk.events)
    if (raw) {
      const data = JSON.parse(raw)
      if (data.eventNodes?.length) {
        const m = migrateEventNodesAndEdges(data.eventNodes, data.eventEdges ?? [])
        const { eventNodes, eventEdges, functions } = sanitizeEventGraph(m.nodes, m.edges, data.functions ?? {})
        return { eventNodes, eventEdges, functions }
      }
    }
    // Migrate from v3 (no functions)
    const oldRaw = localStorage.getItem(sk.eventsOld)
    if (oldRaw) {
      const data = JSON.parse(oldRaw)
      if (data.eventNodes?.length) {
        const m = migrateEventNodesAndEdges(data.eventNodes, data.eventEdges ?? [])
        const { eventNodes, eventEdges } = sanitizeEventGraph(m.nodes, m.edges)
        return { eventNodes, eventEdges, functions: {} }
      }
    }
    // Migrate from face blueprint, then body
    for (const key of [sk.face, sk.body]) {
      const d = JSON.parse(localStorage.getItem(key) || 'null')
      if (d?.eventNodes?.length) {
        const m = migrateEventNodesAndEdges(d.eventNodes, d.eventEdges ?? [])
        const { eventNodes, eventEdges } = sanitizeEventGraph(m.nodes, m.edges)
        return { eventNodes, eventEdges, functions: {} }
      }
    }
  } catch { /* ignore */ }
  return { eventNodes: null, eventEdges: [], functions: {} }
}

function dedupeVarIds(vars) {
  const seen = new Set()
  return vars.map(v => {
    if (!v.id || seen.has(v.id)) {
      const id = `var_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
      seen.add(id)
      return { ...v, id }
    }
    seen.add(v.id)
    return v
  })
}

function loadVariables(sk) {
  try {
    const ev = JSON.parse(localStorage.getItem(sk.events) || 'null')
               ?? JSON.parse(localStorage.getItem(sk.eventsOld) || 'null')
    if (ev?.variables?.length) return dedupeVarIds(ev.variables)
    for (const key of [sk.body, sk.face]) {
      const d = JSON.parse(localStorage.getItem(key) || 'null')
      if (d?.variables?.length) return dedupeVarIds(d.variables)
    }
  } catch { /* ignore */ }
  return []
}

function loadSaved(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const data = JSON.parse(raw)
    // Deduplicate unique event nodes (keeps first occurrence)
    if (data.eventNodes) {
      const seen = new Set()
      data.eventNodes = data.eventNodes.filter(n => {
        if (!UNIQUE_EVENT_TYPES.has(n.type)) return true
        if (seen.has(n.type)) return false
        seen.add(n.type)
        return true
      })
    }
    return data
  } catch { return null }
}

function maxNodeId(nodes) {
  let max = 0
  const scan = (ns) => {
    for (const n of ns) {
      const m = n.id?.match(/^node_(\d+)$/)
      if (m) max = Math.max(max, parseInt(m[1]))
      if (n.data?.innerNodes) scan(n.data.innerNodes)
    }
  }
  scan(nodes)
  return max
}

// ─── Context menu — shown on right-click in the canvas ───────────────────────
function ContextMenu({ screenX, screenY, onAdd, onPickExistingNode, graphType = 'body', onClose }) {
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState({})
  const [tooltip, setTooltip] = useState(null) // { item, x, y }
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  // Rebuild the palette every render rather than relying on a module-load
  // snapshot — plugins register asynchronously after this module evaluates
  // (loadAllPlugins runs in App's mount effect), so module-load NODE_PALETTE
  // never contains plugin entries. The cost is negligible (<20 entries).
  const palette = buildPalette()
  // Space-insensitive and type-aware, matching the event graph's palette. This
  // one matched only label|desc, so "playbodyclip" found nothing at all.
  const q = search.toLowerCase().replace(/\s+/g, '')
  const filtered = palette.filter(n =>
    !q || (n.label + ' ' + n.desc + ' ' + n.type).toLowerCase().replace(/\s+/g, '').includes(q)
  )

  // Nodes of THIS graph that already match — see PaletteGraphMatches. Read per
  // render: the graph refs are module singletons mutated in place.
  const graphs = readGraphs()
  const graphMatches = search.trim()
    ? findGraphNodes(graphs, search, { preferGraph: graphType }).filter(m => m.graph === graphType)
    : []

  // Group by category (preserving first-seen order)
  const categories = []
  const byCategory = {}
  for (const item of filtered) {
    const cat = item.category ?? 'Other'
    if (!byCategory[cat]) { byCategory[cat] = []; categories.push(cat) }
    byCategory[cat].push(item)
  }

  const menuWidth = 210
  const toggleCat = (cat) => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))

  return (
    <AnchoredOverlay
      x={screenX} y={screenY} margin={8}
      style={{
        width: menuWidth,
        background: 'var(--m-panel, #141414)',
        border: '1px solid var(--m-line, #303030)',
        borderRadius: 6,
        boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
        zIndex: 1000,
        fontFamily: 'var(--m-font)',
        maxHeight: 420,
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseDown={e => e.stopPropagation()}
      // Stop clicks inside the menu (search input, category headers) from
      // bubbling to the editor wrapper's onClick, which closes the menu. It
      // should only close on a node selection (onClose) or a click outside it.
      onClick={e => e.stopPropagation()}
    >
      {/* Search */}
      <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid var(--m-line, #1e1e1e)', flexShrink: 0 }}>
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'Enter') {
              // An existing node beats a catalog entry, matching what is shown.
              if (graphMatches.length) onPickExistingNode?.(graphMatches[0])
              else if (filtered.length > 0) onAdd(filtered[0].type)
              else return
              onClose()
            }
          }}
          placeholder="Search nodes..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--m-bg, #0a0a0a)', border: '1px solid var(--m-line, #2a2a2a)',
            color: 'var(--m-fg, #ccc)', padding: '4px 8px', borderRadius: 4,
            fontSize: 11, outline: 'none', fontFamily: 'var(--m-font)',
          }}
        />
      </div>

      {/* Category list */}
      <div style={{ overflowY: 'auto', padding: '4px 0' }}>
        <PaletteGraphMatches
          query={search}
          graphs={graphs}
          graph={graphType}
          onPick={(m) => { onPickExistingNode?.(m); onClose() }}
        />
        {categories.length === 0 && graphMatches.length === 0 && (
          <div style={{ color: 'var(--m-fg-3, #404040)', fontSize: 11, padding: '10px 12px' }}>No results</div>
        )}
        {categories.map(cat => (
          <div key={cat}>
            {/* Category header */}
            <div
              onClick={() => toggleCat(cat)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px',
                cursor: 'pointer',
                color: 'var(--m-fg-3, #666)',
                fontSize: 10,
                letterSpacing: 0.8,
                userSelect: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--m-fg-2, #999)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--m-fg-3, #666)'}
            >
              <span style={{ fontSize: 8 }}>{collapsed[cat] ? '▶' : '▼'}</span>
              {cat.toUpperCase()}
            </div>

            {/* Items */}
            {!collapsed[cat] && byCategory[cat].map(item => (
              <div
                key={item.type}
                onClick={() => { onAdd(item.type); onClose() }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--m-panel-hi, #1f1f1f)'
                  const r = e.currentTarget.getBoundingClientRect()
                  setTooltip({ item, x: r.right + 6, y: r.top })
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent'
                  setTooltip(null)
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px 6px 22px',
                  cursor: 'pointer',
                  borderLeft: `3px solid ${item.color}`,
                  marginLeft: 8, marginRight: 6,
                  borderRadius: '0 3px 3px 0',
                  background: 'transparent',
                }}
              >
                <span style={{ color: item.color, fontSize: 11 }}>{item.label}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Tooltip portal */}
      {tooltip && (
        <AnchoredOverlay portal x={tooltip.x} y={tooltip.y} style={{
          background: 'var(--m-panel, #1e1e1e)',
          border: `1px solid ${tooltip.item.color}44`,
          borderRadius: 4,
          padding: '6px 10px',
          zIndex: 1001,
          maxWidth: 190,
          pointerEvents: 'none',
        }}>
          <div style={{ color: tooltip.item.color, fontSize: 10, fontWeight: 'bold', marginBottom: 3, fontFamily: 'var(--m-font)' }}>
            {tooltip.item.label}
          </div>
          <div style={{ color: 'var(--m-fg-3, #888)', fontSize: 10, fontFamily: 'var(--m-font)', lineHeight: 1.4 }}>
            {tooltip.item.desc}
          </div>
        </AnchoredOverlay>
      )}
    </AnchoredOverlay>
  )
}


// ─── FlowCanvas — lives inside ReactFlowProvider, uses useReactFlow ──────────
function FlowCanvas({
  nodesWithHandlers, edges,
  onNodesChange, onEdgesChange, onConnect, onBeforeDelete,
  addNodeAt,
  onDeleteNode, onDuplicateNode, onArrange,
  clipboardHost = null,
  collabReadOnly = false,
  graphType = 'body',
}) {
  const { screenToFlowPosition, getNodes, getEdges, fitView } = useReactFlow()
  // Serves a Ctrl+F / agent focus request aimed at this graph's TOP level.
  // Keep this canvas's framing across a close (see useGraphViewport).
  const viewportProps = useRememberedViewport({ surface: graphType })

  useCanvasFocus({
    graph: graphType,
    onSelectNode: useCallback(
      (id) => onNodesChange(selectOnlyChanges(nodesWithHandlers, id)),
      [onNodesChange, nodesWithHandlers],
    ),
  })
  const [contextMenu, setContextMenu]     = useState(null)
  const [nodeMenu,    setNodeMenu]        = useState(null)

  // ── Arrange (ENG-1069) ────────────────────────────────────────────────────
  // Compute new positions HERE (getNodes carries the measured sizes + live
  // selection the parent's controlled nodes lack), then hand the patch to the
  // parent so the mutation runs through its setNodes → auto-save + undo history.
  const runArrange = useCallback((op) => {
    if (collabReadOnly || !onArrange) return
    const geo = getNodes().map(n => ({
      id: n.id,
      position: n.position,
      width: n.measured?.width ?? n.width,
      height: n.measured?.height ?? n.height,
      selected: n.selected,
    }))
    const selIds = geo.filter(n => n.selected).map(n => n.id)
    let patches = []
    if (op === 'tidy') patches = tidyGraph(geo, edges, {})
    else if (op === 'straighten') patches = straightenConnections(geo, edges, selIds)
    else if (op.startsWith('align:')) patches = alignNodes(geo, selIds, op.slice(6))
    else if (op.startsWith('distribute:')) patches = distributeNodes(geo, selIds, op.slice(11))
    if (patches.length === 0) return
    onArrange(patches)
    // Only a whole-graph tidy re-frames the view; selection ops leave it put.
    if (op === 'tidy') setTimeout(() => fitView({ padding: 0.3, duration: 300 }), 40)
  }, [collabReadOnly, onArrange, getNodes, edges, fitView])

  // Arrange keyboard chords fire only while the pointer is over THIS canvas
  // (two graph editors can be mounted at once), and never while typing.
  const hoveredRef = useRef(false)
  useEffect(() => {
    const onKey = (e) => {
      if (!hoveredRef.current || collabReadOnly) return
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      const op = arrangeShortcutFor(e)
      if (!op) return
      e.preventDefault()
      runArrange(op)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [collabReadOnly, runArrange])

  // nodeTypes for ReactFlow. Plugins register asynchronously via
  // pluginLoader; if BlueprintEditor mounts first the initial useMemo
  // misses them and ReactFlow renders nothing for plugin nodes. Subscribe
  // to the registry so we re-derive the map (and force ReactFlow to remount
  // the affected nodes) whenever a new plugin type registers.
  const [pluginRegVer, setPluginRegVer] = useState(() => getPluginRegistryVersion())
  useEffect(() => subscribePluginRegistry(() => setPluginRegVer(getPluginRegistryVersion())), [])
  const nodeTypesAll = useMemo(() => buildNodeTypesAll(), [pluginRegVer])

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    setNodeMenu(null)
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  // Toolbar "Search" opens the same add-node palette as a right-click, anchored
  // at the button.
  const openPalette = useCallback((e) => {
    e.stopPropagation() // else the wrapper's onClick closes the menu we just opened
    setNodeMenu(null)
    const x = e.clientX, y = e.clientY
    setContextMenu({ screenX: x, screenY: y, flowPos: screenToFlowPosition({ x, y }) })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])
  const closeNodeMenu = useCallback(() => setNodeMenu(null), [])

  // ── Clipboard (copy / cut / paste) ────────────────────────────────────────
  // The host is assembled HERE because only this component can see the live
  // selection: the parent's controlled `nodes` do not carry `selected`, so a
  // copy driven from up there would always find nothing to copy. Mutations
  // still land in the parent (clipboardHost.applyPaste / removeNodes) so they
  // ride the same setNodes → graphState sync + undo history as every other
  // edit.
  const flowApiRef = useRef(null)
  flowApiRef.current = { screenToFlowPosition }
  const clipboard = useGraphClipboard(
    useMemo(() => ({
      ...(clipboardHost ?? {}),
      getGraph: () => ({ nodes: getNodes(), edges: getEdges() }),
    }), [clipboardHost, getNodes, getEdges]),
    { surface: graphType, enabled: !collabReadOnly && !!clipboardHost, flowRef: flowApiRef },
  )

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu(null)
    setNodeMenu({ screenX: e.clientX, screenY: e.clientY, nodeId: node.id, deletable: node.deletable !== false })
  }, [])

  const handleAdd = useCallback((type) => {
    if (contextMenu) addNodeAt(type, contextMenu.flowPos)
  }, [contextMenu, addNodeAt])

  // Go to a node the user picked out of the palette's "IN THIS GRAPH" section.
  // requestFocus, so a node inside a state machine is drilled into rather than
  // silently unreachable from this canvas.
  const handlePickExistingNode = useCallback((hit) => {
    requestFocus({ graph: graphType, nodeId: hit.nodeId, container: hit.container, label: hit.label })
      .catch(() => {})
  }, [graphType])

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onClick={() => { closeMenu(); closeNodeMenu() }}
      onMouseEnter={() => { hoveredRef.current = true }}
      onMouseLeave={() => { hoveredRef.current = false }}
      onPointerMove={clipboard.onPointerMove}
      onPaste={clipboard.onPaste}
    >
      {/* Canvas toolbar (ENG-1069) — replaces the plain hint text. */}
      <GraphToolbar
        hint="right-click to add · Del to remove · Ctrl+C/X/V to copy nodes"
        onSearch={openPalette}
        onTidy={() => runArrange('tidy')}
      />

      <ReactFlow
        nodes={nodesWithHandlers}
        edges={edges}
        onNodesChange={collabReadOnly ? undefined : onNodesChange}
        onEdgesChange={collabReadOnly ? undefined : onEdgesChange}
        onConnect={collabReadOnly ? undefined : onConnect}
        onBeforeDelete={collabReadOnly ? undefined : onBeforeDelete}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        nodeTypes={nodeTypesAll}
        edgeTypes={EDGE_TYPES}
        {...viewportProps}
        fitViewOptions={{ padding: 0.3 }}
        deleteKeyCode="Delete"
        style={{ background: 'var(--m-bg-graph, #161616)' }}
        nodesDraggable={!collabReadOnly}
        nodesConnectable={!collabReadOnly}
        elementsSelectable={!collabReadOnly}
        edgesReconnectable={!collabReadOnly}
        {...GRAPH_MULTISELECT_PROPS}
      >
        <Background variant={BackgroundVariant.Dots} color="var(--m-line-hi, #333)" gap={20} size={1} />
        <Controls style={{ background: 'var(--m-panel-hi, #222)', border: '1px solid var(--m-line-hi, #444)' }} />
        <MiniMap
          // Display-only (no pannable/zoomable), so it must not intercept the
          // pointer: React Flow's panel container defaults to pointer-events all,
          // which made every node that fitView happens to place under the
          // bottom-right corner silently unclickable. If minimap navigation is
          // ever wanted, add pannable/zoomable AND remove this — an interactive
          // minimap needs the pointer back.
          style={{ background: 'var(--m-panel-hi, #1a1a1a)', border: '1px solid var(--m-line, #333)', pointerEvents: 'none' }}
          nodeColor={n => {
            if (n.type === 'finalPose')        return '#50a0ff'
            if (n.type === 'blend')            return '#40a0e0'
            if (n.type === 'stateMachine')     return '#5070d0'
            if (n.type === 'layeredBoneBlend') return '#90c030'
            return '#e07040'
          }}
        />
      </ReactFlow>

      {contextMenu && (
        <ContextMenu
          screenX={contextMenu.screenX}
          screenY={contextMenu.screenY}
          onAdd={handleAdd}
          onPickExistingNode={handlePickExistingNode}
          graphType={graphType}
          onClose={closeMenu}
        />
      )}
      {nodeMenu && (
        <NodeContextMenu
          screenX={nodeMenu.screenX}
          screenY={nodeMenu.screenY}
          nodeId={nodeMenu.nodeId}
          deletable={nodeMenu.deletable}
          onDelete={onDeleteNode}
          onDuplicate={onDuplicateNode}
          onArrange={runArrange}
          onCopy={clipboardHost ? clipboard.doCopy : undefined}
          onCut={clipboardHost && !collabReadOnly ? clipboard.doCut : undefined}
          onClose={closeNodeMenu}
        />
      )}

      <GraphClipboardStatus status={clipboard.status} />

      {/* Read-only banner when collab is enabled but not yet synced */}
      {collabReadOnly && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
          zIndex: 100,
          pointerEvents: 'none',
        }}>
          <div style={{
            background: 'var(--m-panel-hi, #1a1a1a)',
            border: '1px solid var(--m-line-hi, #404040)',
            borderRadius: 8,
            padding: '16px 32px',
            color: 'var(--m-fg, #ccc)',
            fontSize: 14,
            fontFamily: 'var(--m-font)',
            textAlign: 'center',
          }}>
            🔄 Connecting to live session… editing is paused
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Inner panel — one per blueprint type, keyed so it fully remounts on switch ───
const BlueprintGraph = forwardRef(function BlueprintGraph({ storageKey, targetGraphState, graphType, debugInfo, variables = [], collabReadOnly = false }, ref) {
  // ── Load initial data ──
  // Flag ON: NEVER boot from localStorage (that copy may be stale — the exact bug we
  // are fixing). Start empty and hydrate from the collab doc when it syncs. NOTE:
  // isBlueprintCollabActive() is false at mount (not synced yet), so gating on it here
  // would wrongly fall through to localStorage — gate on the flag alone.
  // Flag OFF: today's localStorage-first behavior, byte-identical.
  const saved = useRef((() => {
    if (isBlueprintCollabCoeditEnabled()) return null
    return loadSaved(storageKey)
  })())

  // ── Node id minting ────────────────────────────────────────────────────────
  //
  // Two things are needed at once, and the old version had only the first:
  //
  //   • MONOTONIC within a render, so two adds batched into one setNodes cannot
  //     both read the same graph and mint the same id. Hence a ref, not a `let`
  //     in the component body — that reset on every render.
  //   • FLOORED BY THE LIVE GRAPH, so it cannot hand back an id that already
  //     exists. The counter used to be seeded once from `saved.current`, the
  //     mount-time localStorage snapshot; a `?blueprint=` URL load or a Load
  //     from Cloud then replaces the graph with one whose ids run higher, and
  //     every subsequent add collided. One add per render usually survived
  //     that (the duplicate node just landed on an existing id and ReactFlow
  //     kept one); a multi-node paste made it immediate and visible.
  //
  // maxOwnedId recurses into innerNodes, so a state machine's inner ids are in
  // the same pool — matching maxNodeId, which this replaces as the seed.
  const nodeIdCounter = useRef(
    saved.current?.nodes ? maxNodeId(saved.current.nodes) + 1 : 1,
  )
  const nextId = () => {
    const live = maxOwnedId(nodesRef.current ?? [], 'node') + 1
    nodeIdCounter.current = Math.max(nodeIdCounter.current, live)
    return `node_${nodeIdCounter.current++}`
  }

  // ── AnimGraph navigation stack ──
  const [viewStack, setViewStack] = useState([{ type: 'main' }])
  const currentView = viewStack[viewStack.length - 1]
  const pushView    = (view) => setViewStack(s => [...s, view])
  const popView     = ()     => setViewStack(s => s.slice(0, -1))

  // ── Main graph state ──
  const [nodes, setNodes, onNodesChange] = useNodesState(saved.current?.nodes ?? INITIAL_NODES)
  const [edges, setEdges, onEdgesChange] = useEdgesState(saved.current?.edges ?? [])

  // ── Focus drill-in (Ctrl+F / agent) ──
  // A request naming a state machine drills into it; one naming a top-level
  // node pops back to 'main' first, so a request is still servable when the
  // user left the editor sitting inside a state machine. Read through a ref:
  // this must not re-subscribe on every node edit. (`nodesRef` further down
  // belongs to the undo history — same trick, different owner.)
  const focusNodesRef = useRef(nodes); focusNodesRef.current = nodes
  const currentContainerId = useCallback(
    () => (currentView.type === 'sm' ? currentView.smNodeId : null),
    [currentView],
  )
  const openFocusContainer = useCallback((containerId) => {
    if (!containerId) { setViewStack([{ type: 'main' }]); return }
    // Only a state machine has a view to open. Anything else (a container from
    // another graph, a stale id) leaves the stack alone so the request times
    // out honestly instead of rendering an empty panel.
    const target = focusNodesRef.current.find(n => n.id === containerId)
    if (target?.type !== 'stateMachine') return
    setViewStack([{ type: 'main' }, { type: 'sm', smNodeId: containerId }])
  }, [])
  useFocusDrill({ graph: graphType, onOpenContainer: openFocusContainer, currentContainerId })

  // ── Auto-save (flag-gated: collab vs. localStorage) ──
  // Flag ON + synced → write to shared doc (no localStorage)
  // Flag ON + not synced → read-only; persist nowhere (no localStorage fallback)
  // Flag OFF → write to localStorage (today's behavior)
  const saveTimer = useRef(null)
  useEffect(() => {
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      const shouldUseCollab = isBlueprintCollabCoeditEnabled() && isBlueprintCollabActive()
      if (shouldUseCollab) {
        writeBlueprintCollab({ [graphType]: { nodes, edges } })
      } else if (!isBlueprintCollabCoeditEnabled()) {
        localStorage.setItem(storageKey, JSON.stringify({ nodes, edges }))
      }
      // else: flag on but not synced → read-only; do NOT write to localStorage
    }, 600)
    return () => clearTimeout(saveTimer.current)
  }, [nodes, edges, storageKey, graphType])

  // Sync nodes/edges to the target graph state (body or face)
  useEffect(() => {
    targetGraphState.nodes = nodes
    targetGraphState.edges = edges
  }, [nodes, edges, targetGraphState])

  // Belt-and-suspenders: ensure all SM nodes have runtime entries on mount,
  // regardless of whether the hydration effect ran before this component.
  useEffect(() => {
    for (const n of nodes) {
      if (n.type === 'stateMachine') initSM(n.id)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update FinalPoseNode connected flag
  useEffect(() => {
    const finalConnected = edges.some(e => e.target === 'final')
    setNodes(ns => {
      const finalNode = ns.find(n => n.id === 'final')
      if (finalNode?.data?.connected === finalConnected) return ns
      return ns.map(n =>
        n.id === 'final' ? { ...n, data: { ...n.data, connected: finalConnected } } : n
      )
    })
  }, [edges]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Node helpers ──
  const updateNodeData = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setNodes])

  const updateSMInnerGraph = useCallback((smNodeId, innerNodes, innerEdges) => {
    setNodes(ns => {
      const updated = ns.map(n =>
        n.id === smNodeId
          ? { ...n, data: { ...n.data, innerNodes, innerEdges } }
          : n
      )
      targetGraphState.nodes = updated
      return updated
    })
  }, [setNodes, targetGraphState])

  const updateTransitionRule = useCallback((smNodeId, edgeId, ruleNodes, ruleEdges) => {
    setNodes(ns => {
      const updated = ns.map(node => {
        if (node.id !== smNodeId) return node
        const newInnerEdges = (node.data.innerEdges ?? []).map(e =>
          e.id !== edgeId ? e
            : { ...e, data: { ...e.data, ruleGraph: { nodes: ruleNodes, edges: ruleEdges } } }
        )
        return { ...node, data: { ...node.data, innerEdges: newInnerEdges } }
      })
      targetGraphState.nodes = updated
      return updated
    })
  }, [setNodes, targetGraphState])

  // ENG-1062: a transition's Actions — custom-event names to fire once when the
  // transition is taken. Sibling of updateTransitionRule; writes edge.data.actions.
  const updateTransitionActions = useCallback((smNodeId, edgeId, actions) => {
    setNodes(ns => {
      const updated = ns.map(node => {
        if (node.id !== smNodeId) return node
        const newInnerEdges = (node.data.innerEdges ?? []).map(e =>
          e.id !== edgeId ? e : { ...e, data: { ...e.data, actions } }
        )
        return { ...node, data: { ...node.data, innerEdges: newInnerEdges } }
      })
      targetGraphState.nodes = updated
      return updated
    })
  }, [setNodes, targetGraphState])

  // ── Connect ──
  const onConnect = useCallback((params) => {
    // Shared Unreal capacity rules (ENG-877). The anim graph has no exec
    // pins, so this is the same replace-on-target behavior as before.
    setEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setEdges])

  // ── Undo history (ENG-878) ──
  const history = useRef(createGraphHistory()).current
  const nodesRef = useRef(nodes); nodesRef.current = nodes
  const edgesRef = useRef(edges); edgesRef.current = edges

  // One snapshot per delete GESTURE: ReactFlow's onBeforeDelete fires once
  // with the full pre-delete state (node + its attached edges together).
  const onBeforeDelete = useCallback(async () => {
    history.push(nodesRef.current, edgesRef.current)
    return true
  }, [history])

  // Apply an arrange patch (ENG-1069). The child computes positions from the
  // measured/selected nodes; the mutation lands here so it rides the same
  // setNodes → graphState sync + undo history as every other edit.
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(nodesRef.current, edgesRef.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setNodes])

  // Ctrl/Cmd+Z restores the last deletion. Document-level (ReactFlow leaves
  // focus on the pane after a delete), guarded to the main view — the SM /
  // rule drill-ins own their own state — and to non-input targets so text
  // fields keep native undo.
  const currentViewRef = useRef(currentView); currentViewRef.current = currentView
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      if (currentViewRef.current?.type !== 'main') return
      const snap = history.pop()
      if (!snap) return
      e.preventDefault()
      setNodes(snap.nodes)
      setEdges(snap.edges)
      // Restored SM nodes need their runtime entries back — the mount-time
      // initSM effect doesn't re-run on undo.
      for (const n of snap.nodes) if (n.type === 'stateMachine') initSM(n.id)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setNodes, setEdges])

  // ── Add node at a specific flow position (from right-click) ──
  const addNodeAt = useCallback((type, position) => {
    if (type === 'playClip') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { clipName: '', loop: false, playSpeed: 1.0 } }])
    } else if (type === 'blend') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { alpha: 0.5 } }])
    } else if (type === 'liveLink') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { clipName: '__livelink__' } }])
    } else if (type === 'firstFramePose' || type === 'lastFramePose') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: {} }])
    } else if (type === 'rigController') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { clipName: '__rigcontrol__', passthrough: false } }])
    } else if (type === 'rigPose') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { poseIndex: 0 } }])
    } else if (type === 'rigMacro') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { macro: 'mouth', alpha: 0 } }])
    } else if (type === 'rigControl') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { control: '', value: 0 } }])
    } else if (type === 'rigBlend') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { alpha: 0.5 } }])
    } else if (type === 'headFollow') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { clipName: '__headfollow__', maxYaw: 45, maxPitch: 25, smoothing: 0.15 } }])
    } else if (type === 'eyeFollow') {
      // 0.7/0.3 is the ticket's Unreal parity; the yaw/pitch limits are
      // gazeLimits.ts's own DEFAULT_GAZE_LIMITS so the ARKit path and a
      // gaze-conditioned eye branch agree on the eye's range.
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { clipName: '__eyefollow__', eyeWeight: 0.7, neckWeight: 0.3, maxYaw: 35, maxPitch: 25, smoothing: 0.35, deadbandDeg: 1 } }])
    } else if (type === 'stateMachine') {
      const id = nextId()
      setNodes(ns => [...ns, {
        id, type, position,
        data: {
          name: 'State Machine',
          innerNodes: [{ id: 'entry', type: 'entry', position: { x: 60, y: 160 }, data: {}, deletable: false }],
          innerEdges: [],
        },
      }])
    } else if (type === 'random') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { randomType: 'float', min: 0, max: 1 } }])
    } else if (type === 'select') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { selectType: 'animation', optionCount: 2, index: 0 } }])
    } else if (type === 'getVariable') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { variableName: '' } }])
    } else if (type === 'layeredBoneBlend') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { blendBone: 'spine_01', blendWeight: 1.0 } }])
    } else if (type === 'blendByBool') {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: { defaultValue: false, trueBlendTime: 0.1, falseBlendTime: 0.1 } }])
    } else if (type.startsWith('plugin.')) {
      // Plugin node: seed dataIn + config defaults from manifest, otherwise empty.
      const entry = getPluginNode(type)
      const dataIn = entry?.nodeManifest?.pins?.dataIn ?? []
      const config = entry?.nodeManifest?.pins?.config ?? []
      const data = {}
      for (const p of [...dataIn, ...config]) if (p.default !== undefined) data[p.id] = p.default
      setNodes(ns => [...ns, { id: nextId(), type, position, data }])
    } else {
      setNodes(ns => [...ns, { id: nextId(), type, position, data: {} }])
    }
  }, [setNodes]) // eslint-disable-line react-hooks/exhaustive-deps

  const deleteNode = useCallback((id) => {
    // Context-menu delete bypasses ReactFlow's delete pipeline — snapshot
    // here so Ctrl+Z restores it too (ENG-878).
    history.push(nodesRef.current, edgesRef.current)
    setNodes(ns => ns.filter(n => n.id !== id))
    setEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setNodes, setEdges, history])

  const duplicateNode = useCallback((id) => {
    setNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, ...cleanData } = node.data
      return [...ns, {
        ...node,
        id: nextId(), // eslint-disable-line react-hooks/exhaustive-deps
        position: { x: node.position.x + 40, y: node.position.y + 40 },
        data: cleanData,
        selected: false,
      }]
    })
  }, [setNodes]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clipboard host (copy / cut / paste) ───────────────────────────────────
  //
  // FlowCanvas owns the gesture (only it can see the live selection); the
  // mutations land here so they ride the same setNodes → graphState sync,
  // localStorage autosave and undo history as every other edit.
  //
  // `mintId` is this graph's own counter rather than a second one: nextId()
  // advances past every node_N in the saved graph INCLUDING nested innerNodes
  // (see maxNodeId), so a pasted state machine cannot collide with anything.
  const applyPastedNodes = useCallback((pastedNodes, pastedEdges) => {
    history.push(nodesRef.current, edgesRef.current)
    // Paste SELECTS what it pasted and deselects the rest — the Unreal
    // behaviour, and what makes an immediate second Ctrl+V (or a drag) act on
    // the new copy rather than the original.
    setNodes(ns => [
      ...(ns ?? []).map(n => (n.selected ? { ...n, selected: false } : n)),
      ...pastedNodes.map(n => ({ ...n, selected: true })),
    ])
    if (pastedEdges?.length) setEdges(es => [...(es ?? []), ...pastedEdges])
    // A restored/pasted state machine needs its runtime entry — the mount-time
    // initSM effect does not re-run for nodes added after mount. Same reason
    // the undo handler does this.
    for (const n of pastedNodes) if (n.type === 'stateMachine') initSM(n.id)
  }, [history, setNodes, setEdges])

  const removePastedNodes = useCallback((ids) => {
    const kill = new Set(ids)
    history.push(nodesRef.current, edgesRef.current)
    setNodes(ns => (ns ?? []).filter(n => !kill.has(n.id)))
    setEdges(es => (es ?? []).filter(e => !kill.has(e.source) && !kill.has(e.target)))
  }, [history, setNodes, setEdges])

  const clipboardHost = useMemo(() => ({
    kind: graphType === 'face' ? 'face' : 'body',
    source: { character: resolveCharacterId?.() ?? null, graph: graphType },
    getDeclarations: () => ({ variables, functions: {} }),
    getAvailable: () => availableRefsFor(graphType === 'face' ? 'face' : 'body', { variables }),
    // Seeded from the LIVE graph, per paste — NOT from nextId(), whose counter
    // comes from the mount-time localStorage snapshot and so collides with a
    // graph loaded from a URL or the cloud.
    makeMinter: () => createMinter(nodesRef.current, 'node'),
    applyPaste: applyPastedNodes,
    removeNodes: removePastedNodes,
  }), [graphType, variables, applyPastedNodes, removePastedNodes]) // eslint-disable-line react-hooks/exhaustive-deps

  // The Rig Preview panel's "Save as pose" inserts a rigPose node from outside
  // the editor; this merges exactly that node into React state (append, dedup
  // by id). Only the FACE editor listens — the body tab has no rig to pose.
  useEffect(() => subscribeFaceGraphInsert(node => {
    if (graphType !== 'face') return
    setNodes(ns => ns?.some(n => n.id === node.id) ? ns : [...(ns || []), node])
  }), [graphType, setNodes])

  // ── Inject handlers + live debug into nodes ──
  // Referentially stable per node (ENG-1068 perf): rebuilds a node's data only
  // when the node object or `debugInfo` changed, so a box-select tick doesn't
  // re-render every node. See useStableNodeData.
  const nodesWithHandlers = useStableNodeData(nodes, n => {
    const graphWeights = graphType === 'body' ? debugInfo.bodyWeights : debugInfo.faceWeights
    const data = {
      onChange: (key, value) => updateNodeData(n.id, key, value),
      _graphType:        graphType,
      _debugWeight:      graphWeights?.[n.data?.clipName] ?? 0,
      _debugAnimRatio:   debugInfo.clipTimings?.[n.data?.clipName]?.ratio ?? 0,
      _debugActiveState: debugInfo.smCurrentState?.[n.id] ?? null,
      _debugClipWeights: graphWeights,
      _debugEventData:   debugInfo.eventData?.[n.type] ?? null,
      _debugLastFired:   debugInfo.nodeLastFired?.[n.id] ?? 0,
    }
    if (n.type === 'headFollow' && debugInfo.headFollow) {
      data._debugYaw   = debugInfo.headFollow.yaw   ?? 0
      data._debugPitch = debugInfo.headFollow.pitch ?? 0
    }
    if (n.type === 'eyeFollow') {
      if (debugInfo.eyeFollow) {
        data._debugYaw   = debugInfo.eyeFollow.yaw   ?? 0
        data._debugPitch = debugInfo.eyeFollow.pitch ?? 0
      }
      // Eye Follow drives ARKit eye morphs, which only a rig character has. On a
      // splat or streamed character the node must SAY so rather than sit there
      // looking live and drive nothing (ENG-1060). The predicates stay in
      // characterState; eyeFollowSupport owns only the precedence + wording.
      const ch = getCurrentCharacter()
      data._unsupportedReason = eyeFollowSupport({
        known: !!ch,
        isSplat: !!ch && isSplatCharacter(ch),
        isStream: !!ch && isStreamCharacter(ch),
      }).reason
    }
    if (n.type === 'stateMachine') {
      data.onDrillIn = () => {
        initSM(n.id)
        pushView({ type: 'sm', smNodeId: n.id })
      }
    }
    if (n.type.startsWith('plugin.')) {
      const entry = getPluginNode(n.type)
      // PluginNode reads `_manifest` to render pins; absent it shows an
      // explicit error card. Stale plugin nodes (plugin uninstalled) will
      // display loudly rather than silently rendering as undefined.
      data._manifest = entry?.nodeManifest ?? null
    }
    return data
  }, [debugInfo, graphType, updateNodeData, pushView])

  // ── Reset ──

  const resetBlueprint = useCallback(() => {
    if (!window.confirm('Reset blueprint? This cannot be undone.')) return
    localStorage.removeItem(storageKey)
    setNodes(INITIAL_NODES)
    setEdges([])
    setViewStack([{ type: 'main' }])
    _nodeId = 1 // eslint-disable-line react-hooks/exhaustive-deps
  }, [setNodes, setEdges, storageKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Expose current data for export (bypasses debounce) ──
  useImperativeHandle(ref, () => ({
    getData: () => ({ nodes, edges, variables }),
    resetCurrent: resetBlueprint,
  }), [nodes, edges, variables, resetBlueprint])

  const smNode = currentView.smNodeId
    ? nodes.find(n => n.id === currentView.smNodeId)
    : null

  const ruleEdge = (currentView.type === 'rule' && smNode)
    ? (smNode.data.innerEdges ?? []).find(e => e.id === currentView.edgeId)
    : null

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {currentView.type === 'rule' && smNode && (
        <TransitionRuleEditor
          smName={smNode.data.name ?? 'State Machine'}
          ruleGraph={ruleEdge?.data?.ruleGraph}
          variables={variables}
          activeClipName={(() => {
            // Use edge source state (not the currently active state) as the clip context
            const sourceStateId = ruleEdge?.source ?? null
            const stateNode = (smNode.data.innerNodes ?? []).find(n => n.id === sourceStateId)
            const sEdges = stateNode?.data?.innerEdges ?? []
            const sNodes = stateNode?.data?.innerNodes ?? []
            const outEdge = sEdges.find(e => e.target === 'output')
            const playNode = outEdge ? sNodes.find(n => n.id === outEdge.source && n.type === 'playClip') : null
            return playNode?.data?.clipName ?? null
          })()}
          onBack={popView}
          onRuleChange={(rNodes, rEdges) =>
            updateTransitionRule(currentView.smNodeId, currentView.edgeId, rNodes, rEdges)
          }
          actions={ruleEdge?.data?.actions ?? []}
          availableCustomEvents={customEventNames(eventGraphState.nodes)}
          onActionsChange={(a) =>
            updateTransitionActions(currentView.smNodeId, currentView.edgeId, a)
          }
        />
      )}

      {currentView.type === 'sm' && smNode && (
        <StateMachineEditor
          smNodeId={currentView.smNodeId}
          smName={smNode.data.name ?? 'State Machine'}
          initialNodes={smNode.data.innerNodes ?? []}
          initialEdges={smNode.data.innerEdges ?? []}
          graphType={graphType}
          onBack={popView}
          onGraphChange={(iNodes, iEdges) =>
            updateSMInnerGraph(currentView.smNodeId, iNodes, iEdges)
          }
          onOpenRule={(edgeId) =>
            pushView({ type: 'rule', smNodeId: currentView.smNodeId, edgeId })
          }
          ruleResults={debugInfo.ruleResults}
        />
      )}

      {currentView.type === 'main' && (
        <ReactFlowProvider>
          <FlowCanvas
            nodesWithHandlers={nodesWithHandlers}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onBeforeDelete={onBeforeDelete}
            addNodeAt={addNodeAt}
            onDeleteNode={deleteNode}
            onDuplicateNode={duplicateNode}
            onArrange={onArrange}
            clipboardHost={clipboardHost}
            collabReadOnly={collabReadOnly}
            graphType={graphType}
          />
        </ReactFlowProvider>
      )}
    </div>
  )
})

/**
 * EditorCommands — the editor's half of the app menubar, with no UI of its own.
 *
 * This used to BE the menubar (a `MenuBar` component rendering File / Character /
 * Animation / Assets / Build / View plus the blueprint tab strip, inside the
 * editor panel). That ownership was the bug: the menubar could not exist before
 * the editor did and vanished whenever it unmounted, which is exactly why a
 * second floating menu had to exist at App level. See src/components/AppMenuBar.jsx.
 *
 * Now the menubar is mounted once by the app shell, and this component only
 * PUBLISHES what the editor can do into the menubarCommands registry. Anything it
 * doesn't publish renders in the menubar as a disabled item naming the reason, so
 * a missing editor is visible rather than a dead click.
 *
 * It still renders one thing: the cloud sync/load progress panel, which is
 * fixed-position and belongs to the operation rather than to any menu.
 */
function EditorCommands({ activeBP, onSetActiveBP, onExport, onImport, onResetCurrent,
                          canResetCurrent, onShowWizard, onLoadFromCloud,
                          collabSynced = false,
                          activeCharId: activeCharIdProp }) {
  const [syncState, setSyncState] = useState(null) // null = idle; { steps: [{id, label, status, detail}] }
  const activeCharId = activeCharIdProp ?? ''

  // The library blueprint the editor currently has loaded, by name, or null
  // when it is editing the character's default (the ordinary case). Read from
  // the asset registry rather than re-fetched: this is a label, and a failed
  // lookup must degrade to "no name" rather than to a wrong one.
  const activeBlueprintNameFor = useCallback((charId) => {
    try {
      const id = getActiveBlueprintId(charId)
      if (!id) return null
      const rec = assetRegistry.list().find(a => a.kind === 'blueprint_asset' && a.rawId === id)
      return rec?.name ?? null
    } catch { return null }
  }, [])

  // saveToCloud syncs the active character's pending uploads + draft +
  // blueprints to the server. Accepts an optional `overrideCharId` so
  // callers (e.g. the wizard) can sync a freshly-created character before
  // BlueprintEditor's own activeCharId state has caught up.
  const saveToCloud = useCallback(async (overrideCharId) => {
    function stripBlobMaterialUrls(materials) {
      if (!materials || typeof materials !== 'object') return materials
      const out = {}
      for (const [role, slots] of Object.entries(materials)) {
        out[role] = {}
        for (const [key, val] of Object.entries(slots ?? {})) {
          out[role][key] = typeof val === 'string' && val.startsWith('blob:') ? null : val
        }
      }
      return out
    }

    // Shim: callers below pass percent-as-number onProgress, headers last.
    function postWithProgress(url, fd, onProgress, headers = {}) {
      return postFileWithProgress(url, fd, {
        headers,
        onProgress: ({ fraction }) => {
          if (fraction >= 0) onProgress(Math.round(fraction * 100))
        },
      })
    }

    // overrideCharId lets the wizard sync a brand-new character before
    // App's character switch has propagated into our local activeCharId.
    const charId = (typeof overrideCharId === 'string' && overrideCharId) ? overrideCharId : activeCharId
    if (!charId) {
      console.warn('[Sync] No active character')
      return false
    }

    const draft = getDraft()

    // Build step list based on what actually needs to happen
    const steps = []
    const pendingRig = getPendingRigFile()
    const hasBlolbRig = draft?.rigPath?.startsWith('blob:')
    console.log(`[Sync] start — charId=${charId} rigPath=${draft?.rigPath?.substring(0,40)} pendingRig=${!!pendingRig} hasBlobRig=${hasBlolbRig}`)
    if (pendingRig && hasBlolbRig) steps.push({ id: 'rig', label: 'Rig GLB', status: 'pending', detail: '' })
    if (!pendingRig && hasBlolbRig) console.warn('[Sync] rig is blob: but pendingRigFile is gone — rig will be stripped, not uploaded')

    const pendingTextureFiles = getPendingTextureFiles()
    const blobTextures = []
    for (const [role, slots] of Object.entries(draft?.materials ?? {})) {
      for (const [key, val] of Object.entries(slots ?? {})) {
        if (typeof val === 'string' && val.startsWith('blob:')) {
          blobTextures.push({ role, key, slotKey: `${role}.${key}` })
        }
      }
    }
    console.log(`[Sync] blob textures: ${blobTextures.length > 0 ? blobTextures.map(t => t.slotKey).join(', ') : 'none'}`)
    console.log(`[Sync] pending texture files: ${Object.keys(pendingTextureFiles).join(', ') || 'none'}`)
    if (blobTextures.length > 0) {
      steps.push({ id: 'textures', label: `Textures (${blobTextures.length})`, status: 'pending', detail: '' })
    }

    steps.push({ id: 'character', label: 'Character record', status: 'pending', detail: '' })
    steps.push({ id: 'blueprints', label: 'Blueprints', status: 'pending', detail: '' })

    setSyncState({ title: 'Save to Cloud', steps })

    const updateStep = (id, status, detail = '') =>
      setSyncState(s => s ? ({
        ...s,
        steps: s.steps.map(st => st.id === id ? { ...st, status, detail } : st),
      }) : s)

    // ── Step: Rig upload ──────────────────────────────────────────────────────
    if (pendingRig && hasBlolbRig) {
      updateStep('rig', 'running')
      try {
        const fd = new FormData()
        fd.append('file', pendingRig)
        const { url } = await postWithProgress(
          `${AAM_BASE}/api/characters/${charId}/rig`,
          fd,
          pct => updateStep('rig', 'running', `${pct}%`),
          AAM_KEY ? { 'X-API-Key': AAM_KEY } : {},
        )
        // The upload returns a raw S3 URL that 403s on direct fetch; store the
        // proxied path so the in-session avatar reload (and the saved record)
        // use the working URL — not the S3 one that triggered the
        // "Couldn't load this character" screen post-sync.
        syncDraftToCharacterStore({ rigPath: toProxiedAssetUrl(url) })
        clearPendingRigFile()
        updateStep('rig', 'done')
        console.log('[Sync] rig uploaded:', url)
      } catch (err) {
        updateStep('rig', 'error', err.message)
        console.error('[Sync] rig upload failed:', err.message)
      }
    }

    // ── Step: Texture uploads ─────────────────────────────────────────────────
    if (blobTextures.length > 0) {
      updateStep('textures', 'running')
      let uploaded = 0
      for (const { role, key, slotKey } of blobTextures) {
        const file = pendingTextureFiles[slotKey]
        if (!file) {
          console.warn(`[Sync] ${slotKey}: pending file lost — texture will be cleared in DB`)
          continue
        }
        try {
          const ext = file.name.match(/\.[a-z0-9]+$/i)?.[0]?.toLowerCase() ?? '.png'
          const fd = new FormData()
          fd.append('name', `${slotKey.replace(/[^a-z0-9._-]/gi, '_')}${ext}`)
          fd.append('file', file)
          const { url } = await postWithProgress(
            `${AAM_BASE}/api/characters/${charId}/textures/${encodeURIComponent(slotKey)}`,
            fd,
            pct => updateStep('textures', 'running', `${slotKey} ${pct}%`),
            AAM_KEY ? { 'X-API-Key': AAM_KEY } : {},
          )
          updateDraft({ materials: { [role]: { [key]: toProxiedAssetUrl(url) } } })
          clearPendingTextureFile(slotKey)
          uploaded++
          console.log(`[Sync] texture uploaded: ${slotKey} → ${url}`)
        } catch (err) {
          console.error(`[Sync] texture upload failed: ${slotKey} —`, err.message)
        }
      }
      updateStep('textures', 'done', `${uploaded}/${blobTextures.length}`)
    }

    // ── Step: Character record ────────────────────────────────────────────────
    // Guard: the draft must belong to the active character. The previous
    // global-draft design allowed this state when a user created character
    // A in the wizard, switched to character B, then hit Sync — the draft
    // still held A's data and got POSTed under B's id, overwriting B with
    // A's fields (myra-renamed-to-Luna-Rig bug). Per-character drafts plus
    // this guard make that impossible. See docs/UX_PRINCIPLES.md.
    updateStep('character', 'running')
    let saved = false
    const latest = getDraft()
    if (!latest) {
      updateStep('character', 'done', 'no changes')
      saved = true
      console.log('[Sync] character record: no draft to sync')
    } else if (latest.id && latest.id !== charId) {
      const msg = `draft id "${latest.id}" ≠ active character "${charId}" — refusing to send`
      updateStep('character', 'error', msg)
      console.error('[Sync]', msg)
    } else {
      try {
        const draftToSend = { ...latest, id: charId }
        if (draftToSend.rigPath?.startsWith('blob:')) draftToSend.rigPath = null
        // Do NOT round-trip the seed-time legacy material columns on sync. The
        // asset-manager re-seeds material_assets from `materials` on upsert, so
        // sending a stale/empty value WIPES the first-class material_assets the
        // user actually edits (materials vanished after "save changes";
        // textures — saved via their own rows — survived, which pinpoints the
        // material-only reconcile). material_assets / texture_assets /
        // material_mapping are the runtime source of truth and persist through
        // their own endpoints (CLAUDE.md: these legacy columns are inert at
        // runtime, seed-time only).
        delete draftToSend.materials
        delete draftToSend.meshSlots
        delete draftToSend.mesh_slots
        delete draftToSend.materialKind
        delete draftToSend.material_kind
        delete draftToSend.materialColor
        delete draftToSend.material_color
        await upsertCharacter(draftToSend)
        saved = true
        updateStep('character', 'done')
        console.log('[Sync] character record saved')
      } catch (err) {
        updateStep('character', 'error', err.message)
        console.error('[Sync] character POST failed:', err.message)
      }
    }

    // ── Step: Blueprints ──────────────────────────────────────────────────────
    updateStep('blueprints', 'running')
    const keys = getStorageKeysFor(charId)
    const bodyData   = JSON.parse(localStorage.getItem(keys.body)   || 'null')
    const faceData   = JSON.parse(localStorage.getItem(keys.face)   || 'null')
    const eventsData = JSON.parse(localStorage.getItem(keys.events) || 'null')
    // A graph with NO local cache carries the SERVER's current copy through
    // unchanged — never a fabricated `{}` (ENG-991). The old `bodyData ?? {}`
    // is how eyeline got bricked (inferred from the broken version's shape):
    // this browser had only event-graph edits — no body/face cache, the face
    // key having just been renamed by the v6→v7 bump — so the sync POSTed
    // body:{} face:{} over the server's full graphs, deleting the OGS render
    // chain. The server row is replace-not-merge, and the all-empty guard
    // below didn't fire because events was non-empty. The fetch only happens
    // when a cache is actually missing (the normal all-cached save costs
    // nothing extra); assetManagerApi.saveBlueprint's payload guard backstops
    // whatever still ends up empty.
    let serverBlueprint = null
    if (bodyData == null || faceData == null || eventsData == null) {
      // 404 = an authoritative "no row yet" → {} (an ANSWER: the server holds
      // nothing, so an empty graph destroys nothing). Any other failure → null
      // (UNKNOWN — confirms nothing, the guard stays strict; a network blip
      // must never open the destructive path).
      serverBlueprint = await getLatestBlueprint(charId).catch((e) =>
        String(e?.message ?? '').includes('404') ? {} : null)
      if (serverBlueprint) console.warn('[Sync] missing local cache for some graph(s) — carrying the server copy through instead of overwriting with an empty graph (ENG-991)')
    }
    const localGraphs = { body: bodyData, face: faceData, events: eventsData }
    const blueprintDataToSave = mergeBlueprintForSave(localGraphs, serverBlueprint)
    // Nothing local to save → SKIP, never fabricate an empty server row. A
    // sync from a browser with no local edits (fresh device, or keys scoped
    // under a different user id) used to POST {body:{}, face:{}, events:{}},
    // creating an all-empty blueprint that then out-raced and clobbered the
    // real graphs on every subsequent load (the 2026-07-01 main-CI outage).
    // A skip counts as success for the overall sync result (there was nothing
    // to save; the server copy is intentionally left untouched).
    let blueprintSaved = true
    if (isEmptyBlueprintData(blueprintDataToSave)) {
      updateStep('blueprints', 'done')
      console.warn('[Sync] no local blueprint data for this character — skipping blueprint save (refusing to overwrite the server copy with an empty graph)')
    } else {
      blueprintSaved = await saveBlueprintToDB(charId, blueprintDataToSave)
      if (blueprintSaved) {
        // Name WHERE it landed when a library blueprint is open (ENG-1030).
        // With several blueprints per character, "saved" alone leaves the one
        // question that matters unanswered — and the answer is not the
        // character's default whenever the editor has one loaded.
        const activeName = activeBlueprintNameFor(charId)
        updateStep('blueprints', 'done', activeName ? `→ ${activeName}` : '')
        console.log(`[Sync] blueprints saved${activeName ? ` → "${activeName}"` : ''}`)
      } else {
        updateStep('blueprints', 'error')
        console.error('[Sync] blueprint save failed')
      }
    }

    // ── Post-sync cleanup ─────────────────────────────────────────────────────
    // Hot-reload materials BEFORE stripping blob: URLs — they're still valid this session.
    const finalDraft = getDraft()
    if (window.__RELOAD_MATERIALS__ && finalDraft) window.__RELOAD_MATERIALS__(finalDraft)
    clearCharactersCache()

    // On a fully successful sync the server now holds the canonical record.
    // Mark the draft as saved — clean signal flips, dirty pill clears, the
    // unsaved-changes modal won't fire on next switch. The draft DATA stays
    // populated so the Material Editor doesn't go blank (Fix #1 / the
    // server-as-truth UX rules in docs/UX_PRINCIPLES.md treat the editor's
    // working copy and the dirty signal as separate concerns).
    //
    // On a partial failure we leave the draft alone (DIRTY state retained),
    // so the user can retry the sync without losing their work.
    if (saved && blueprintSaved) {
      markDraftSaved()
      // After a successful push, the DB is now authoritative. Drop the cached
      // blueprint drafts so they can't silently shadow the server on the next
      // visit (the stale-localStorage bug Florent hit). The CLEAR is what
      // prevents that bug — NOT a page reload. Do NOT window.location.reload()
      // here: the in-memory editor state already matches what we just pushed,
      // materials were hot-reloaded via __RELOAD_MATERIALS__ above, and a full
      // reload mid-edit is jarring (the regression this fixes). Just clear the
      // sync toast after a beat.
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('aos-blueprint')) localStorage.removeItem(k)
      }
      setTimeout(() => setSyncState(null), 3000)
    } else if (finalDraft) {
      // Partial failure — strip blob: URLs that are about to expire so a
      // page reload doesn't try to refetch them.
      if (finalDraft.rigPath?.startsWith('blob:')) updateDraft({ rigPath: null })
      if (finalDraft.materials) updateDraft({ materials: stripBlobMaterialUrls(finalDraft.materials) })
    }

    return saved && blueprintSaved
  }, [activeCharId])

  // Pull the latest blueprint from the cloud. Confirms via the app dialog
  // (never the browser's), then reports progress in the same panel Save uses —
  // so the user gets a clear "working… → done" instead of nodes silently
  // popping in. The actual data work is the parent's onLoadFromCloud.
  const handleLoadFromCloud = useCallback(async () => {
    const ok = await dialog.confirm({
      title: 'Load from Cloud?',
      body: 'This replaces your local Body, Face, and Event graphs with the version saved on the server. Any local changes you have NOT saved to the cloud will be lost.',
      confirmLabel: 'Load from Cloud',
      cancelLabel: 'Cancel',
      danger: true,
    })
    if (!ok) return
    setSyncState({ title: 'Load from Cloud', steps: [{ id: 'load', label: 'Loading from cloud', status: 'running', detail: 'fetching latest version…' }] })
    try {
      const r = await onLoadFromCloud?.()
      if (r?.superseded) {
        // The character changed while the fetch was in flight; that character's
        // own load owns the graphs now. Reporting "restored" would be a lie.
        setSyncState(null)
        return
      }
      if (r?.empty) {
        setSyncState({ title: 'Load from Cloud', steps: [{ id: 'load', label: 'Nothing to load', status: 'error', detail: 'no blueprint saved to the cloud yet' }] })
        setTimeout(() => setSyncState(null), 3500)
        return
      }
      setSyncState({ title: 'Load from Cloud', steps: [{ id: 'load', label: 'Restored from cloud', status: 'done', detail: 'body, face & events updated' }] })
      setTimeout(() => setSyncState(null), 2500)
    } catch (e) {
      console.error('[LoadFromCloud] failed:', e)
      setSyncState({ title: 'Load from Cloud', steps: [{ id: 'load', label: 'Load failed', status: 'error', detail: String(e?.message ?? e) }] })
      setTimeout(() => setSyncState(null), 4500)
    }
  }, [onLoadFromCloud])

  // Expose for the unsaved-changes modal in App.jsx — when the user picks
  // "Save" the modal awaits this and only proceeds with the pending switch
  // once it resolves true.
  useEffect(() => {
    window.__SYNC_TO_CLOUD__ = saveToCloud
    return () => { if (window.__SYNC_TO_CLOUD__ === saveToCloud) delete window.__SYNC_TO_CLOUD__ }
  }, [saveToCloud])


  // ── Publish into the menubar ─────────────────────────────────────────────
  // One effect, re-run whenever any published handler or state value changes, so
  // the menubar's enabled/checked states track the editor without prop drilling
  // through App. The cleanup unregisters, which is what turns every editor item
  // in the menubar back into "blueprint editor not open".
  useEffect(() => menubarCommands.register('blueprint-editor', {
    commands: {
      'file.exportGraph': () => onExport?.(),
      // Takes the change event: the menubar owns the hidden <input> (it owns the
      // menu row), the editor owns what an imported bundle means.
      'file.importGraph': (e) => onImport?.(e),
      'file.saveToCloud': () => saveToCloud(),
      'file.loadFromCloud': () => handleLoadFromCloud(),
      'file.flushCollab': () => flushCollabBlueprint(),
      'file.resetGraph': () => onResetCurrent?.(),
      'character.createNew': () => onShowWizard?.(true),
      'editors.open': (id) => onSetActiveBP?.(id),
      'editors.close': () => onSetActiveBP?.(null),
    },
    state: {
      activeBP,
      canResetGraph: !!canResetCurrent,
      collabEnabled: isBlueprintCollabCoeditEnabled(),
      collabSynced,
    },
  }), [
    onExport, onImport, saveToCloud, handleLoadFromCloud, onResetCurrent,
    onShowWizard, onSetActiveBP, activeBP, canResetCurrent, collabSynced,
  ])

  return (
    <>
      {syncState && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
          background: '#13141e', border: '1px solid #3a3a6e', borderRadius: 8,
          padding: '12px 16px', fontFamily: 'var(--m-font)', fontSize: 11,
          minWidth: 220, boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
        }}>
          <div style={{ fontSize: 9, color: 'var(--m-fg-3, #555)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
            {syncState.title ?? 'Save to Cloud'}
          </div>
          {syncState.steps.map(s => (
            <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5 }}>
              <span style={{ width: 12, textAlign: 'center', color:
                s.status === 'done' ? '#40a060' :
                s.status === 'error' ? '#a04040' :
                s.status === 'running' ? '#8b6cff' : '#333'
              }}>
                {s.status === 'done' ? '✓' : s.status === 'error' ? '✗' : s.status === 'running' ? '…' : '·'}
              </span>
              <span style={{ color:
                s.status === 'done' ? '#60c080' :
                s.status === 'error' ? '#e06060' :
                s.status === 'running' ? '#c0a0ff' : '#555'
              }}>
                {s.label}
              </span>
              {s.detail && (
                <span style={{ color: 'var(--m-fg-3, #555)', marginLeft: 2 }}>{s.detail}</span>
              )}
            </div>
          ))}
          {/* Indeterminate "working" bar while any step runs. A true percent
              isn't meaningful for a sub-second fetch, so this is an honest
              animated sweep rather than a fake progress %. */}
          {syncState.steps.some(s => s.status === 'running') && (
            <div style={{ marginTop: 8, height: 3, background: 'var(--m-panel-hi, #222)', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, bottom: 0, width: '35%', background: '#8b6cff', borderRadius: 2, animation: 'aosCloudIndet 1.1s ease-in-out infinite' }} />
              <style>{'@keyframes aosCloudIndet { 0% { left: -35%; } 100% { left: 100%; } }'}</style>
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ─── Top-level editor — selects between Body, Face, and Event Graph ───
// Memoized — takes no props, so parent re-renders (App.jsx setTextInput,
// setAvatarInfo, setFrameStats, setWsState) should not cascade into the
// editor's render tree. Internal setState from the debugState poll still
// triggers re-render, which is what we want.
// The View-menu props this used to take (onPopOut / onToggleLiveView /
// onToggleTune / onToggleAnim and their flags) are gone: those are App-owned
// concerns and App now passes them straight to <AppMenuBar>, instead of routing
// them down through the editor purely so the editor's menubar could show them.
export const BlueprintEditor = memo(function BlueprintEditor({ initialActiveBP = null, onSwitchCharacter, activeCharId: activeCharIdProp,
  // Window chrome from the docked-shell host (App): drag props spread on the
  // surface header (which doubles as the window's title bar — one bar, not two
  // stacked ones) and the float/dock/pop-out/pop-in buttons rendered before
  // the ✕. (The old standalone-popup mode is gone — popping out portals THIS
  // instance into the OS window, chrome and all.)
  windowFrame = null }) {
  // null = no editor surface open (the shell shows the viewport). The docked
  // shell has no tab strip: a surface opens from the Character / Animation
  // menus and closes back to the viewport with the X in its header.
  const [activeBP, setActiveBP] = useState(initialActiveBP)
  const [mountKey, setMountKey] = useState(0)
  const [showWizard, setShowWizard] = useState(false)
  const activeBPRef = useRef(null)
  // Ctrl+F find-a-node box. One instance for all three graph surfaces — it
  // searches the live module refs, so it needs nothing from the canvas below it.
  const [searchOpen, setSearchOpen] = useState(false)
  const isGraphSurface = activeBP === 'body' || activeBP === 'face' || activeBP === 'events'

  // Ctrl+F (⌘F on mac) opens the find box while a graph surface is up, and
  // preventDefault keeps the BROWSER's find bar out of the way — its text
  // search over a canvas would find nothing, which is what made "where is that
  // node?" a question people had to ask a person.
  useEffect(() => {
    if (!isGraphSurface) { setSearchOpen(false); return }
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.code === 'KeyF') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isGraphSurface])

  // A focus request can only be served by a mounted canvas. Closing the editor
  // (or switching to a non-graph surface) settles the pending one as cancelled
  // rather than leaving the caller waiting on its timeout.
  useEffect(() => { if (!isGraphSurface) cancelFocus() }, [isGraphSurface])

  // ── Active character + reactive storage keys ──
  const initialCharId = resolveCharacterId()
  const [activeCharId, setActiveCharId] = useState(initialCharId)
  const sk = useMemo(() => getStorageKeysFor(activeCharId), [activeCharId])
  // True once a CHARACTER-SCOPED cloud read has actually applied graphs. Read
  // from async callbacks that closed over an older render, to keep the
  // first-run default from landing on top of a cloud blueprint that already
  // won (see the default branch in the mount effect below). Deliberately NOT
  // "is a character selected": a character whose cloud read returns nothing
  // (404 / new character) must still fall through to the shipped default.
  const cloudGraphsAppliedRef = useRef(false)

  // ── Blueprint co-editing client (flag-gated) ──
  const { me } = useAuth()
  const blueprintClientRef = useRef(null)
  const [collabSynced, setCollabSynced] = useState(false)

  useEffect(() => {
    if (!isBlueprintCollabCoeditEnabled() || !activeCharId || !me?.org_id) {
      blueprintClientRef.current?.destroy()
      blueprintClientRef.current = null
      registerCollabBlueprintClient(null)
      setCollabSynced(false)
      return
    }

    let cancelled = false

    Promise.all([
      import('../lib/CollabBlueprintClient.js'),
      import('../lib/collabPresence.js')
    ]).then(([{ CollabBlueprintClient }, { resolveCollabUrl, roomName }]) => {
      if (cancelled) return

      const client = new CollabBlueprintClient()
      blueprintClientRef.current = client

      client.connect({
        url: resolveCollabUrl(import.meta.env),
        room: `${me.org_id}:${activeCharId}:blueprint`,
        user: { id: me.id, name: me.email || me.id, color: '#5080c0' }
      })

      registerCollabBlueprintClient(client)

      console.log(`[BlueprintEditor] Connected to blueprint collab: ${me.org_id}:${activeCharId}:blueprint`)
    }).catch(err => console.error('[BlueprintEditor] Blueprint collab connect failed:', err))

    return () => {
      cancelled = true
      blueprintClientRef.current?.destroy()
      blueprintClientRef.current = null
      registerCollabBlueprintClient(null)
      setCollabSynced(false)
    }
  }, [activeCharId, me?.org_id])

  // Subscribe to sync state changes for read-only banner
  useEffect(() => {
    if (!isBlueprintCollabCoeditEnabled()) {
      setCollabSynced(false)
      return
    }
    return onCollabSyncChange((synced) => {
      setCollabSynced(synced)
    })
  }, [])

  // Track when we're applying a remote blueprint change to prevent echo loops
  const applyingRemoteRef = useRef(false)

  // Register remote blueprint change handler (GAP 1: bidirectional sync)
  // Applies incoming converged graphs to editor state without echoing back
  useEffect(() => {
    if (!isBlueprintCollabCoeditEnabled()) {
      setRemoteBlueprintHandler(null)
      return
    }

    // Handler applies remote blueprint changes to all three graph states
    const handleRemoteBlueprint = (blueprint) => {
      applyingRemoteRef.current = true
      try {
        // Extract state updates from the incoming blueprint (pure decision logic)
        const updates = computeRemoteApplyState(blueprint)

        // Apply body graph
        if (updates.bodyState) {
          graphState.nodes = updates.bodyState.nodes
          graphState.edges = updates.bodyState.edges
        }

        // Apply face graph
        if (updates.faceState) {
          faceGraphState.nodes = updates.faceState.nodes
          faceGraphState.edges = updates.faceState.edges
        }

        // Apply event graph (migrated — a remote peer on an older build can
        // still converge a graph carrying retired types like beginPlay)
        if (updates.eventState) {
          const m = migrateEventNodesAndEdges(updates.eventState.nodes, updates.eventState.edges)
          eventGraphState.nodes = m.nodes
          eventGraphState.edges = m.edges
          eventGraphState.functions = updates.eventState.functions
          notifyEventGraphChange()
        }

        // Sync variables if present
        if (updates.variables) {
          setVariables(updates.variables)
        }
      } finally {
        applyingRemoteRef.current = false
      }
    }

    setRemoteBlueprintHandler(handleRemoteBlueprint)
    return () => setRemoteBlueprintHandler(null)
  }, [])

  // Compute read-only state: flag on + not synced → read-only
  const collabReadOnly = isBlueprintCollabCoeditEnabled() && !collabSynced

  // ── Shared event graph state (single instance across body/face) ──
  const _initEv = useRef(loadEvents(getStorageKeysFor(initialCharId)))
  const [eventNodes, setEventNodes] = useState(_initEv.current.eventNodes)
  const [eventEdges, setEventEdges] = useState(_initEv.current.eventEdges)
  const [functions,  setFunctions]  = useState(_initEv.current.functions ?? {})
  const [variables, setVariables] = useState(() => loadVariables(getStorageKeysFor(initialCharId)))

  // Auto-patch: if the saved event graph is missing the onTextInput →
  // sendTextInput chain, inject it once. The shipped blueprint JSON has
  // this wired up, but if the URL load fails (or localStorage is from a
  // pre-text-input version), typing in the chat box silently no-ops
  // because no event node matches. This makes the chat input work
  // out-of-the-box for demos / fresh installs.
  useEffect(() => {
    setEventNodes(prev => {
      if (prev?.some(n => n.type === 'onTextInput')) return prev
      console.log('[BlueprintEditor] event graph missing onTextInput chain — auto-injecting')
      const onTextNode = {
        id: 'ev_on_text_input_auto',
        type: 'onTextInput',
        position: { x: 80, y: 80 },
        data: {},
      }
      const sendNode = {
        id: 'act_send_text_auto',
        type: 'sendTextInput',
        position: { x: 360, y: 80 },
        data: {},
      }
      setEventEdges(prevEdges => ([
        ...(prevEdges || []),
        {
          id: 'e_on_text_to_send_auto',
          source: onTextNode.id,
          sourceHandle: 'execOut',
          target: sendNode.id,
          targetHandle: 'execIn',
        },
        // Wire onTextInput's text data pin into sendTextInput's text input
        {
          id: 'e_on_text_to_send_data_auto',
          source: onTextNode.id,
          sourceHandle: 'text',
          target: sendNode.id,
          targetHandle: 'in-text',
        },
      ]))
      return [...(prev || []), onTextNode, sendNode]
    })
  }, [])  // run once on mount

  // ── Reload blueprints when active character changes (no page reload) ──
  const isFirstMount = useRef(true)
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return }
    const newSk = getStorageKeysFor(activeCharId)
    const ev = loadEvents(newSk)
    setEventNodes(ev.eventNodes)
    setEventEdges(ev.eventEdges)
    setFunctions(ev.functions ?? {})
    setVariables(loadVariables(newSk))
    setMountKey(k => k + 1) // remounts BlueprintGraph with new storageKey

    // Cloud is authoritative (ENG-797/798): on every character switch, pull the
    // server blueprint and let it override the localStorage-seeded state set
    // above. Stale localStorage must never shadow the cloud — that shadowing is
    // exactly what dropped the voxy mic and forced "Load from Cloud every time".
    // An explicit ?blueprint= URL still overrides (that async load owns the
    // graphs; a DB read here would race and clobber it).
    const hasBlueprintParam = !!new URLSearchParams(window.location.search).get('blueprint')
    if (resolveEditorBlueprintSource({ hasBlueprintParam }).kind === 'db' && activeCharId) {
      loadBlueprintFromDB(activeCharId).then(result => {
        if (result?.superseded) return // another character took over mid-flight
        if (!result) return // no DB blueprint — blank canvas is intentional
        cloudGraphsAppliedRef.current = true
        if (result.evSrc) {
          setEventNodes(result.evSrc.eventNodes ?? [])
          setEventEdges(result.evSrc.eventEdges ?? [])
          if (result.evSrc.functions) setFunctions(result.evSrc.functions)
        }
        if (result.variables?.length) setVariables(result.variables)
        setMountKey(k => k + 1) // remount graphs now that state reflects the cloud
      }).catch(() => {})
    }
  }, [activeCharId])

  // ── Switch character without page reload ──
  // charConfig can be passed for wizard-created drafts so App.jsx skips the
  // network fetch and sets the character state directly.
  //
  // IMPORTANT: this function MUST NOT commit any side effects (localStorage
  // writes, internal state updates) — App.jsx owns the dirty-draft prompt
  // and decides whether the switch proceeds. If we wrote `aos-character-v1`
  // here, a Cancel in the modal would still leave the user on the new
  // character on next reload.
  const switchToCharacter = useCallback((id, charConfig) => {
    onSwitchCharacter?.(charConfig ?? id)
  }, [onSwitchCharacter])

  // App.jsx owns the dirty-draft prompt and only commits the switch once
  // the user confirms. When the commit happens, the new activeCharId
  // arrives via prop — sync it into internal state and reset the blueprint
  // storage-key cache so the per-character keys swap correctly.
  useEffect(() => {
    if (activeCharIdProp && activeCharIdProp !== activeCharId) {
      setCurrentCharacterId(activeCharIdProp)
      resetStorageKeys()
      setActiveCharId(activeCharIdProp)
    }
  }, [activeCharIdProp]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync variables to animVariables
  useEffect(() => {
    syncVariables(variables)
  }, [variables])

  // Sync to EventRuntime
  useEffect(() => {
    if (eventNodes) {
      eventGraphState.nodes = eventNodes
      eventGraphState.edges = eventEdges
      notifyEventGraphChange()
    }
  }, [eventNodes, eventEdges])

  useEffect(() => {
    eventGraphState.functions = functions
  }, [functions])

  // A node inserted from outside the editor (CameraRigNotice's live "Add Orbit Rig") must land
  // in React state, else the sync effects above overwrite eventGraphState with the rig-less
  // eventNodes and the camera goes dead again. EventGraph handles this when its canvas is
  // mounted; this covers the case where a different tab is showing, so the node is present the
  // next time the event graph opens (and gets auto-saved to localStorage meanwhile).
  useEffect(() => subscribeEventGraphInsert(node => {
    setEventNodes(ns => ns?.some(n => n.id === node.id) ? ns : [...(ns || []), node])
  }), [])

  // Auto-save events + variables (flag-gated: collab vs. localStorage)
  // Flag ON + synced → write to shared doc (no localStorage)
  // Flag ON + not synced → read-only; persist nowhere (no localStorage fallback)
  // Flag OFF → write to localStorage (today's behavior)
  const evSaveTimer = useRef(null)
  useEffect(() => {
    clearTimeout(evSaveTimer.current)
    evSaveTimer.current = setTimeout(() => {
      // Use pure decision logic to determine if local edit should route to collab doc
      const routeToDoc = shouldRouteEditToDoc(
        isBlueprintCollabCoeditEnabled(),
        isBlueprintCollabActive(),
        applyingRemoteRef.current
      )

      if (routeToDoc) {
        writeBlueprintCollab({ events: { eventNodes, eventEdges, variables, functions } })
      } else if (!isBlueprintCollabCoeditEnabled()) {
        localStorage.setItem(sk.events, JSON.stringify({ eventNodes, eventEdges, variables, functions }))
      }
      // else: flag on but not synced → read-only; do NOT write to localStorage
    }, 600)
    return () => clearTimeout(evSaveTimer.current)
  }, [eventNodes, eventEdges, variables, functions, sk.events])

  // ── Blueprint auto-loader ──
  // Cloud is authoritative (ENG-797/798). On open:
  //   • `/?blueprint=<path>.json` — load the shipped file (reset / override).
  //   • `/` with a character        — read the AAM cloud snapshot. localStorage
  //                                    is NEVER the boot source, so a stale
  //                                    local cache can't shadow the cloud graph
  //                                    (the bug that dropped the voxy mic and
  //                                    forced "Load from Cloud every time").
  //   • `/` with no character       — the first-run STARTER for the rig kind.
  // localStorage remains a write-through cache (autosave) + "Save to Cloud"
  // makes edits durable; it is just no longer read on boot.
  //
  // Resolved through STARTER_TEMPLATES (ENG-1022) rather than spelled out here,
  // so there is ONE place that answers "what does a character of this kind start
  // from". It resolves to the same conversational.json this line used to name —
  // the point is that changing it later is a registry edit, not a hunt for
  // hardcoded literals across App and the editor, which is precisely how the
  // splat default came to be one character's own file.
  const DEFAULT_BLUEPRINT = starterTemplateUrl(resolveStarterKind({ splat: false }))
  const [urlBpLoaded, setUrlBpLoaded] = useState(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const bpUrl = params.get('blueprint')

    if (resolveEditorBlueprintSource({ hasBlueprintParam: !!bpUrl }).kind === 'url') {
      // Explicit ?blueprint= URL — always load and wipe localStorage
      loadBlueprint(bpUrl, { characterId: activeCharId ?? initialCharId ?? null })
        .then(({ evSrc, variables: importedVars }) => {
          if (evSrc) {
            setEventNodes(evSrc.eventNodes)
            setEventEdges(evSrc.eventEdges ?? [])
            if (evSrc.functions) setFunctions(evSrc.functions)
          }
          if (importedVars?.length) setVariables(importedVars)
          console.log(`[Blueprint] Loaded from ?blueprint=${bpUrl}`)
        })
        .catch(err => console.error('[Blueprint] URL load failed:', err))
        .finally(() => setUrlBpLoaded(true))
      return
    }

    // Shared by the two "nothing authored anywhere" paths below. The applyIf
    // gate drops the default if a cloud blueprint managed to apply while the
    // static file was in flight (see the comment on the no-character branch).
    // claimOwnership: false — initialCharId is captured at MOUNT and this only
    // fetches once a 404 comes back, by which time the user may have switched.
    // The id still picks the storage keys; re-arming it would drop the fresh
    // load for whoever is on screen now.
    const loadShippedDefault = () =>
      loadBlueprint(DEFAULT_BLUEPRINT, {
        applyIf: () => !cloudGraphsAppliedRef.current,
        characterId: initialCharId ?? null,
        claimOwnership: false,
      })
        .then(({ evSrc, variables: importedVars, superseded }) => {
          if (superseded) {
            console.log('[Blueprint] first-run default superseded by cloud blueprint')
            return
          }
          if (evSrc) {
            setEventNodes(evSrc.eventNodes)
            setEventEdges(evSrc.eventEdges ?? [])
            if (evSrc.functions) setFunctions(evSrc.functions)
          }
          if (importedVars?.length) setVariables(importedVars)
          console.log('[Blueprint] Loaded first-run default')
        })
        .catch(err => console.error('[Blueprint] Default load failed:', err))

    if (initialCharId) {
      // Cloud authoritative: always read the server blueprint on open, ignoring
      // localStorage as a boot SOURCE.
      loadBlueprintFromDB(initialCharId)
        .then(result => {
          if (!result) {
            // No cloud blueprint (404 / brand-new character). If localStorage
            // carries a cached graph for this character — the write-through
            // cache, including the creation wizard's intentionally-blank seed
            // (writeEmptyBlueprintsForCharacter) — the initial state already
            // shows it and it must stay: blank-with-rig is an authored state,
            // not an absence. Only a character with no graph ANYWHERE falls
            // through to the shipped default — what the editor has always done
            // for a never-authored character. (Before the launcher, that case
            // arrived via the no-character branch below, because the editor
            // could mount before the character list resolved; a ?character=
            // deep link resolves the id at mount, so this branch has to carry
            // the fallback itself now.)
            const hasCachedGraph = [sk.body, sk.face, sk.events].some(k => {
              try {
                const g = JSON.parse(localStorage.getItem(k) || 'null')
                return (g?.nodes?.length ?? 0) > 0 || (g?.eventNodes?.length ?? 0) > 0
              } catch { return false }
            })
            if (!hasCachedGraph) return loadShippedDefault()
            return // cached graph (possibly deliberately blank) — keep it
          }
          // Superseded: another character claimed the graphs mid-flight, so
          // nothing was applied. Claiming otherwise would suppress the shipped
          // default and log a load that never happened.
          if (result.superseded) return
          cloudGraphsAppliedRef.current = true
          if (result.evSrc) {
            setEventNodes(result.evSrc.eventNodes ?? [])
            setEventEdges(result.evSrc.eventEdges ?? [])
            if (result.evSrc.functions) setFunctions(result.evSrc.functions)
          }
          if (result.variables?.length) setVariables(result.variables)
          console.log(`[Blueprint] Loaded from DB for character: ${initialCharId}`)
        })
        .catch(err => console.error('[Blueprint] DB load failed:', err))
        .finally(() => setUrlBpLoaded(true))
      return
    }

    // No character at all (first-run / no character selected) → load the default
    //
    // A character's cloud blueprint may land while this default is in flight.
    // Applying the shipped default on top would clobber the user's saved
    // graph — precisely the "stale/default shadows the cloud" failure
    // ENG-797/798 exists to prevent, and autosave would then write it back.
    //
    // This branch is chosen from `initialCharId` captured at mount, which is
    // null whenever the character list hasn't resolved yet (fresh profile, no
    // ?character=). The two loads never cancelled each other, so pure
    // ordering picked the winner: usually the cloud read landed last and all
    // was well, but when this static file lost the race the editor silently
    // showed conversational.json instead of the saved graph.
    //
    // Gate on "the cloud actually APPLIED graphs", not "a character exists":
    // a character with no cloud blueprint (404 / brand new) must still get
    // the shipped default here, which is what the editor has always done.
    //
    // The gate is passed INTO loadBlueprint (applyIf), not checked here in the
    // .then: loadBlueprint hydrates the runtime graph refs + localStorage as a
    // side effect before resolving, so a .then-side check "dropped" the late
    // default while the runtime and localStorage were already clobbered.
    loadShippedDefault().finally(() => setUrlBpLoaded(true))
  }, [])

  const exportBlueprint = useCallback(() => {
    const activeData = activeBP !== 'events' ? (activeBPRef.current?.getData() ?? null) : null
    const bodyData = activeBP === 'body' ? activeData
      : JSON.parse(localStorage.getItem(sk.body) || 'null')
    const faceData = activeBP === 'face' ? activeData
      : JSON.parse(localStorage.getItem(sk.face) || 'null')
    // `variables` MUST be included: the events graph declares the vars the body
    // and face graphs read (getVariable, Blend alphaVariable, Blend by Bool
    // boolVariable, SM rules). Without them a blueprint imported onto ANOTHER
    // character has dangling variable references and the author has to remake
    // every variable by hand. Import already reads evSrc.variables — this is the
    // write half of that round-trip. (Same shape as the sk.events autosave.)
    const evData = { eventNodes, eventEdges, variables, functions }
    const blob = new Blob([JSON.stringify({ body: bodyData, face: faceData, events: evData }, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = 'blueprint.json'
    a.click()
    URL.revokeObjectURL(url)
  }, [activeBP, eventNodes, eventEdges, variables])

  const importBlueprint = useCallback((e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result)
        if (data.body || data.face || data.events) {
          if (data.body)   localStorage.setItem(sk.body, JSON.stringify(data.body))
          if (data.face)   localStorage.setItem(sk.face, JSON.stringify(data.face))
          // Load events: prefer dedicated key, fall back to face then body for old format
          const evSrcRaw = data.events
            ?? (data.face?.eventNodes?.length ? { eventNodes: data.face.eventNodes, eventEdges: data.face.eventEdges ?? [] } : null)
            ?? (data.body?.eventNodes?.length ? { eventNodes: data.body.eventNodes, eventEdges: data.body.eventEdges ?? [] } : null)
          // Hydration boundary — an exported file can carry retired types
          // (beginPlay) or a right-button camera pan, so the same migration
          // pass as every other load runs.
          let evSrc = null
          if (evSrcRaw) {
            const m = migrateEventNodesAndEdges(evSrcRaw.eventNodes ?? [], evSrcRaw.eventEdges ?? [])
            evSrc = { ...evSrcRaw, eventNodes: m.nodes, eventEdges: m.edges }
          }
          if (evSrc) {
            localStorage.setItem(sk.events, JSON.stringify(evSrc))
            setEventNodes(evSrc.eventNodes)
            setEventEdges(evSrc.eventEdges ?? [])
            if (evSrc.functions) setFunctions(evSrc.functions)
          }
          // Load variables: prefer events, fall back to body/face
          const importedVars = evSrc?.variables ?? data.body?.variables ?? data.face?.variables
          if (importedVars?.length) setVariables(importedVars)
        } else if (data.nodes) {
          const activeKey = activeBP === 'body' ? sk.body : sk.face
          localStorage.setItem(activeKey, JSON.stringify(data))
        }
        setMountKey(k => k + 1)
      } catch (err) {
        console.error('[Blueprint] Import failed:', err)
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }, [activeBP])

  // Pull the latest blueprint FROM the server, replacing the local copy. This
  // is the explicit "Load from Cloud" counterpart to "Save to Cloud" — the
  // normal load path is localStorage-first (DB only when local is empty), so
  // without this the editor can never re-sync a diverged/stale local cache.
  //
  // Data work ONLY — the confirm prompt + progress feedback live in EditorCommands
  // (which owns the dialog + the progress panel). Returns a status so the
  // caller can report it. Throws on real failure.
  const loadFromCloud = useCallback(async () => {
    if (!activeCharId) return { empty: true }
    const result = await loadBlueprintFromDB(activeCharId)
    // Superseded (the character changed while this was in flight) is not an
    // empty cloud — say nothing rather than report a wrong answer.
    if (result?.superseded) return { superseded: true }
    if (!result) return { empty: true }
    // loadBlueprintFromDB hydrates body/face into localStorage; mirror the
    // event-graph + variables hydration here and remount so the canvas shows
    // the freshly-pulled graphs.
    if (result.evSrc) {
      setEventNodes(result.evSrc.eventNodes ?? [])
      setEventEdges(result.evSrc.eventEdges ?? [])
      if (result.evSrc.functions) setFunctions(result.evSrc.functions)
    }
    if (result.variables?.length) setVariables(result.variables)
    setMountKey(k => k + 1)
    return { ok: true }
  }, [activeCharId])

  // ── Hydrate both anim graph states on load ──
  useEffect(() => {
    if (!urlBpLoaded) return
    const bodyData = loadSaved(sk.body)
    const faceData = loadSaved(sk.face)
    if (bodyData) {
      graphState.nodes = bodyData.nodes ?? []
      graphState.edges = bodyData.edges ?? []
      for (const n of graphState.nodes) { if (n.type === 'stateMachine') initSM(n.id) }
    }
    if (faceData) {
      faceGraphState.nodes = faceData.nodes ?? []
      faceGraphState.edges = faceData.edges ?? []
      for (const n of faceGraphState.nodes) { if (n.type === 'stateMachine') initSM(n.id) }
    }
  }, [urlBpLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll debugState every 100ms ──
  // Only setDebugInfo when GraphEvaluator/EventRuntime have actually written
  // something since the last poll (tracked via debugState.tick). Skipping the
  // setState call avoids re-rendering the whole editor at 10Hz when idle.
  const [debugInfo, setDebugInfo] = useState({ bodyWeights: {}, faceWeights: {}, smCurrentState: {}, ruleResults: {}, eventData: {}, nodeLastFired: {}, clipTimings: {}, headFollow: null, eyeFollow: null })
  const lastTickRef = useRef(-1)
  useEffect(() => {
    const id = setInterval(() => {
      if (debugState.tick === lastTickRef.current) return
      lastTickRef.current = debugState.tick
      tracePhase('bp.pollSetDebug', () => setDebugInfo({
        bodyWeights:    { ...debugState.bodyWeights },
        faceWeights:    { ...debugState.faceWeights },
        smCurrentState: { ...debugState.smCurrentState },
        ruleResults:    { ...debugState.ruleResults },
        eventData:      { ...debugState.eventData },
        nodeLastFired:  { ...debugState.nodeLastFired },
        clipTimings:    { ...debugState.clipTimings },
        headFollow:     debugState.headFollow ? { ...debugState.headFollow } : null,
        eyeFollow:      { ...debugState.eyeFollow },
      }))
    }, 100)
    return () => clearInterval(id)
  }, [])

  // The per-tab accent colours are gone with the selector bar: the menubar's tab
  // strip marks the active tab with the palette accent like every other selected
  // control, so a second per-tab colour scheme would fight the theme.
  const bpOptions = [
    { id: 'body', label: 'Body', storageKey: sk.body, target: graphState },
    { id: 'face', label: 'Face', storageKey: sk.face, target: faceGraphState },
  ]

  const activeBPConfig = bpOptions.find(b => b.id === activeBP)

  const handleResetCurrent = useCallback(() => {
    activeBPRef.current?.resetCurrent()
  }, [])

  const commands = (
    <EditorCommands
      activeBP={activeBP}
      onSetActiveBP={setActiveBP}
      onExport={exportBlueprint}
      onImport={importBlueprint}
      onResetCurrent={handleResetCurrent}
      canResetCurrent={activeBP === 'body' || activeBP === 'face'}
      onShowWizard={setShowWizard}
      onLoadFromCloud={loadFromCloud}
      collabSynced={collabSynced}
      activeCharId={activeCharId}
    />
  )

  // The wizard is menu-launched (Character > Create new...), so it must work
  // with NO surface open — it renders whether or not a panel is up.
  const wizard = showWizard && (
    <CharacterCreationWizard
      onCreated={config => {
        setShowWizard(false)
        switchToCharacter(config.id, config)
      }}
      onCancel={() => setShowWizard(false)}
    />
  )

  // No surface open: the editor is HEADLESS — mounted for its registry
  // publication, cloud sync (__SYNC_TO_CLOUD__), and blueprint hydration, while
  // the shell shows the viewport. This replaces the old always-visible right
  // panel; nothing here may render chrome.
  if (!activeBP) {
    return <>{commands}{wizard}</>
  }

  const SURFACE_TITLES = {
    body: 'BODY BLUEPRINT', face: 'FACE BLUEPRINT', events: 'EVENT GRAPH',
    prefab: 'PREFAB EDITOR', animator: 'ANIMATOR', clips: 'CLIPS',
    'audio-assets': 'AUDIO CLIPS',
  }

  return (
    <div
      data-testid="editor-surface"
      data-surface={activeBP}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--m-bg, #0f0f0f)' }}
    >
      {commands}

      {/* Surface header — names the editor and closes back to the viewport.
          This header IS the window's title bar: drag it to move the floated
          editor (windowFrame.dragProps), and the window buttons
          (float/dock/pop-out, or pop-in while portalled into an OS window)
          sit beside the ✕. */}
      <div
        onPointerDown={windowFrame?.dragProps?.onPointerDown}
        style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px',
        background: 'var(--m-panel)', borderBottom: '1px solid var(--m-line)',
        flexShrink: 0, userSelect: 'none',
        ...(windowFrame?.dragProps?.style ?? {}),
      }}>
        <span style={{ fontFamily: 'var(--m-font)', fontSize: 9, letterSpacing: '.14em', color: 'var(--m-fg)' }}>
          {SURFACE_TITLES[activeBP] ?? activeBP.toUpperCase()}
        </span>
        <span style={{ flex: 1 }} />
        {windowFrame?.buttons}
        <button
          type="button"
          data-testid="editor-surface-close"
          onClick={() => setActiveBP(null)}
          title="Close editor — back to the viewport"
          style={{
            background: 'transparent', border: '1px solid var(--m-line-hi)',
            color: 'var(--m-fg-2)', borderRadius: 4, padding: '2px 8px',
            fontSize: 11, fontFamily: 'var(--m-font)', cursor: 'pointer', lineHeight: 1.4,
          }}
        >✕</button>
      </div>

      {/* ── Panel ── */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {searchOpen && isGraphSurface && (
          <GraphSearchBox surface={activeBP} onClose={() => setSearchOpen(false)} />
        )}
        {activeBP === 'animator' ? (
          <AnimatorEditorPanel />
        ) : activeBP === 'prefab' ? (
          <PrefabEditorPanel />
        ) : activeBP === 'audio-assets' ? (
          <AudioAssetsPanel />
        ) : activeBP === 'events' ? (
          // Gated + keyed exactly like BlueprintGraph below (ENG-876):
          // EventGraph seeds its canvas from props ONCE (useNodesState), so a
          // character switch or cloud load must REMOUNT it — without the key
          // the canvas kept showing the previous character's graph until F5.
          urlBpLoaded && (
            <EventGraph
              key={`events-${activeCharId}-${mountKey}`}
              variables={variables}
              onVariablesChange={setVariables}
              eventNodes={eventNodes}
              eventEdges={eventEdges}
              onEventNodesChange={setEventNodes}
              onEventEdgesChange={setEventEdges}
              functions={functions}
              onFunctionsChange={setFunctions}
              nodeLastFired={debugInfo.nodeLastFired}
            />
          )
        ) : activeBPConfig ? (urlBpLoaded && (
          <BlueprintGraph
            key={activeBPConfig.id + '-' + mountKey}
            ref={activeBPRef}
            storageKey={activeBPConfig.storageKey}
            targetGraphState={activeBPConfig.target}
            graphType={activeBPConfig.id}
            debugInfo={debugInfo}
            variables={variables}
            collabReadOnly={collabReadOnly}
          />
        )) : null}
      </div>

      {wizard}
    </div>
  )
})
